// src/components/Dashboard/Dashboard.js
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { app, auth, API_BASE_URL }  from '../../firebase/firebaseConfig';
import { getAuth, signOut } from 'firebase/auth';
import { collection, collectionGroup, doc, getCountFromServer, getDoc, getDocs, getFirestore, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { getHistoryDocsNewestFirst } from '../../lib/sessionHistoryQueries';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DeviceAccountGroup, OrphanedDevicesGroup } from './DeviceAccountGroup';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import HotplugSettingsViewer from './HotplugSettingsViewer';
import OverrideSettingsViewer from './OverrideSettingsViewer';
import InstantLogViewer from './InstantLogViewer';
import SettingsViewer from './SettingsViewer';
import { HEARTH_SCOPE, IGLOO_SCOPE } from './settingsScopes';
import TvInfoViewer from './TvInfoViewer';
import HealthTab from './HealthTab';
import SessionsTab from './SessionsTab';
import StatusTab from './StatusTab';
import AdminSupportChat from '../Support/AdminSupportChat';
import MessageInbox from '../Support/MessageInbox';
import DocumentViewer from './DocumentViewer';
import BillingModal from './BillingModal';
import LogViewer from './LogViewer';
import AlertsViewer from './AlertsViewer';
import IglooAccountGroup from './IglooAccountGroup';
import { useIglooNames } from './useIglooNames';
import { useSessionStorageState } from './useSessionStorageState';
import { extractIglooId } from './locationDisplay';
import { useAllConversations, useAllAdminDrafts } from '../Support/useAdminSupportMessages';
import UsageTimeline from './UsageTimeline';
import { deriveTier, accountCalls, accountLastMultiPersonCallMs, formatCallDuration, TIERS, TIER_NUMBERS, toMs as usageToMs } from './usageTiers';
import AccountTypeFilter, { ACCOUNT_TYPES, OTHER_ACCOUNT_TYPE, accountTypeKey, isAccountTypeVisible } from './AccountTypeFilter';
import BillingAccountsTable from './BillingAccountsTable';
import { deriveBillingRecord, billingStateOrder } from './billingRecord';
import { useAccountComments } from './useAccountComments';
import AccountCommentCell from './AccountCommentCell';
import { useAccountMessageActivity } from './useAccountMessageActivity';
import { useAccountDailyStates } from './useAccountDailyStates';
import HistoryTimeline, { HistoryAxisHeader, HistoryLegend } from './HistoryTimeline';
import {
  accountMilestones,
  accountDailyStates,
  accountEarliestMs,
  resolveHistoryWindow,
  isCustomRangeValid,
  formatElapsed,
  formatShortDate,
  toHistoryMs,
  toMonthStr,
  HISTORY_RANGES,
  DEFAULT_HISTORY_RANGE,
} from './historyEvents';
import {
  LogOut,
  Users,
  Monitor,
  Plus,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Download,
  AlertTriangle,
  User,
  Home,
  Settings,
  Snowflake,
  ChevronDown,
  ChevronRight,
  Gamepad2,
  Upload,
  FileText,
  Activity,
  MessageCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Mail,
  Play,
  BarChart3,
  PackageCheck,
  Package,
  Plug,
  RotateCcw,
  CreditCard,
  RefreshCw,
} from 'lucide-react';

// Helper to format relative time (e.g. "3d ago", "12h ago")
function formatRelativeTime(timestampMs) {
  if (!timestampMs) return null;
  const now = Date.now();
  const diffMs = now - timestampMs;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Width of the History view's timeline column, shared by the axis header and
// every row so their gridlines line up exactly.
const HISTORY_TIMELINE_WIDTH = 620;

// The History axis end advances with the clock, but only in quarter-hour steps
// so a long-open dashboard stays current without re-rendering every table row
// once a minute.
const QUARTER_HOUR_MS = 15 * 60 * 1000;
function roundToQuarterHour(ms) {
  return Math.floor(ms / QUARTER_HOUR_MS) * QUARTER_HOUR_MS;
}

// WiFi signal strength bars icon (1-4 bars based on RSSI dBm)
function WifiSignalBars({ rssiDbm }) {
  let bars;
  let color;
  if (rssiDbm > -50) {
    bars = 4; color = '#22c55e';
  } else if (rssiDbm > -60) {
    bars = 3; color = '#22c55e';
  } else if (rssiDbm > -70) {
    bars = 2; color = '#f97316';
  } else {
    bars = 1; color = '#ef4444';
  }

  const barWidth = 2.5;
  const gap = 1.5;
  const heights = [4, 7, 10, 13];
  const totalWidth = 4 * barWidth + 3 * gap;

  return (
    <svg
      width={totalWidth}
      height={14}
      viewBox={`0 0 ${totalWidth} 14`}
      className="flex-shrink-0"
      title={`WiFi RSSI: ${Math.round(rssiDbm)} dBm`}
    >
      {heights.map((h, i) => (
        <rect
          key={i}
          x={i * (barWidth + gap)}
          y={14 - h}
          width={barWidth}
          height={h}
          rx={0.5}
          fill={i < bars ? color : '#d1d5db'}
        />
      ))}
    </svg>
  );
}

function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newNumFamilies, setNewNumFamilies] = useState(2);
  const [newAccountType, setNewAccountType] = useState('freeHearthOwningAccount');
  const [newBatch, setNewBatch] = useState('');
  const [newCountry, setNewCountry] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [resetShipping, setResetShipping] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  // The active top tab comes from the URL (/:tab) — TabRoute in App.js
  // guarantees a valid value. Tab switches navigate (push), so browser
  // back/forward walk tab history.
  const { tab: activeTab } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [devices, setDevices] = useState([]);
  const [availableAppVersions, setAvailableAppVersions] = useState([]);
  const [availableOsVersions, setAvailableOsVersions] = useState([]);
  const [logModalVisible, setLogModalVisible] = useState(false);
  const [logContent, setLogContent] = useState('');
  const [logLoading, setLogLoading] = useState(false);
  const [currentLogName, setCurrentLogName] = useState('');
  const [currentLogFamilyID, setCurrentLogFamilyID] = useState(null);
  const [currentLogPath, setCurrentLogPath] = useState(null);
  const [logReloading, setLogReloading] = useState(false);
  const [isCrashLog, setIsCrashLog] = useState(false);
  const logTimestampBeforeReload = useRef(null);
  const [igloos, setIgloos] = useState([]);
  const [igloosLoaded, setIgloosLoaded] = useState(false);          // first iglooInfo snapshot arrived
  const [iglooAccountsLoaded, setIglooAccountsLoaded] = useState(false); // accountsInfo grouping read finished
  // Redesigned igloos tab: account-grouped list (by controller family) + per-igloo
  // video backend (W2) flip + a full-page live-metrics view.
  const [familyIdToAccount, setFamilyIdToAccount] = useState({});      // familyId -> { accountId, email }
  const [expandedIglooAccounts, setExpandedIglooAccounts] = useState({}); // accountId -> bool
  const [iglooAccountsError, setIglooAccountsError] = useState(false); // accountsInfo grouping read failed
  const [liveSessionsByIgloo, setLiveSessionsByIgloo] = useState({});  // iglooId -> live participant count
  const [showInstantLogViewer, setShowInstantLogViewer] = useState(false);
  const [selectedInstantLogDevice, setSelectedInstantLogDevice] = useState(null);
  // Global (baseline) settings editor: null = closed, otherwise the scope being
  // edited (HEARTH_SCOPE on the devices tab, IGLOO_SCOPE on the igloos tab).
  const [settingsViewerScope, setSettingsViewerScope] = useState(null);
  const [hotplugFamilies, setHotplugFamilies] = useState(new Set());
  const [showHotplugViewer, setShowHotplugViewer] = useState(false);
  const [alertUnreadByFamily, setAlertUnreadByFamily] = useState({});
  // Null = closed. { familyId: null } = cross-family inbox. { familyId, familyName } = per-device.
  const [alertsViewerCtx, setAlertsViewerCtx] = useState(null);
  const [selectedHotplugDevice, setSelectedHotplugDevice] = useState(null);
  const [overrideFamilies, setOverrideFamilies] = useState(new Set());
  // Igloos with at least one active override field (drives the per-igloo settings
  // button's active styling, mirroring overrideFamilies for devices).
  const [overrideIgloos, setOverrideIgloos] = useState(new Set());
  // Override editor: null = closed, otherwise { scope, entity:{ id, name } }.
  // One piece of state serves both the Hearth and Higlo scopes.
  const [overrideViewerCtx, setOverrideViewerCtx] = useState(null);
  const [showTvInfoViewer, setShowTvInfoViewer] = useState(false);
  const [selectedTvInfoDevice, setSelectedTvInfoDevice] = useState(null);
  const [importFileType, setImportFileType] = useState('csv');
  const [importAccountType, setImportAccountType] = useState('freeHearthOwningAccount');
  const [isImporting, setIsImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [deviceAccounts, setDeviceAccounts] = useState([]);
  const [expandedDeviceAccounts, setExpandedDeviceAccounts] = useSessionStorageState('higlo.admin.expandedAccounts.devices', {});
  const [allFamilyNames, setAllFamilyNames] = useState({});
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [selectedChatUser, setSelectedChatUser] = useState(null);
  const [showMessageInbox, setShowMessageInbox] = useState(false);
  const [signedInCounts, setSignedInCounts] = useState({});
  const [sortField, setSortField] = useState('email');
  const [sortDirection, setSortDirection] = useState('asc');
  const [deviceRssi, setDeviceRssi] = useState({});
  const [deviceLocation, setDeviceLocation] = useState({}); // deviceId -> live location string (from accountVisible.location)
  const [firstSigninTimes, setFirstSigninTimes] = useState({});
  // iglooId -> friendly name, for resolving igloo locations on the device rows.
  // Only fetched while the Devices tab is active; shared with the Health tab.
  // Feed the hook the igloo IDs actually on screen so it can resolve any that
  // appeared after the initial scan (a device entering a brand-new igloo).
  const neededIglooIds = useMemo(
    () => Array.from(new Set(Object.values(deviceLocation).map(extractIglooId).filter(Boolean))),
    [deviceLocation]
  );
  const iglooNames = useIglooNames(activeTab === 'devices', neededIglooIds);
  const [deviceSortMode, setDeviceSortMode] = useState('alphabetic');
  const [lastIglooTimes, setLastIglooTimes] = useState({});
  const [iglooTimesLoading, setIglooTimesLoading] = useState(false);
  const [showShipSubscribeModal, setShowShipSubscribeModal] = useState(false);
  const [shipSubscribeUserId, setShipSubscribeUserId] = useState(null);
  const [shipUpdateDate, setShipUpdateDate] = useState(true);
  const [docViewerTarget, setDocViewerTarget] = useState(null); // { type: 'account'|'family', id: string }
  const [billingCheckTarget, setBillingCheckTarget] = useState(null); // { id: string, email: string }
  const [billingAttention, setBillingAttention] = useState({}); // { accountUUID: [reason, ...] }
  const [billingAttentionDegraded, setBillingAttentionDegraded] = useState(false); // sweep failed/partial — dots may be missing
  // Accounts tab presentation + usage analysis
  const [viewMode, setViewMode] = useSessionStorageState('higlo.admin.accounts.viewMode', 'default'); // 'default' | 'usage' | 'billing' | 'history'
  // The History view's shared time window. One setting for the whole table —
  // every row is drawn against the same axis, which is the point of the view.
  const [historyRange, setHistoryRange] = useSessionStorageState('higlo.admin.accounts.historyRange', DEFAULT_HISTORY_RANGE);
  const [tierFilter, setTierFilter] = useSessionStorageState('higlo.admin.accounts.tierFilter', {}); // { [tierNumber]: true } — empty = show all
  // Hidden account types. Shared across ALL three views on purpose: switching
  // view must never change which accounts are on screen.
  const [typeFilter, setTypeFilter] = useSessionStorageState('higlo.admin.accounts.typeFilter', {}); // { [accountTypeKey]: true } — empty = show all
  const [usageAnalytics, setUsageAnalytics] = useState({}); // { [familyId]: usageAnalytics doc }
  const [analyticsMeta, setAnalyticsMeta] = useState(null); // usageAnalytics/_meta doc
  const [analyticsRunning, setAnalyticsRunning] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(false); // last update-analysis run failed
  const [analyticsFetchDegraded, setAnalyticsFetchDegraded] = useState(false); // reading usageAnalytics failed — data may be missing/stale

  const allFamilyNamesRef = useRef({}); // cached family names for deviceManagement listener
  const deviceVisibleRef = useRef({}); // { deviceId: { ...visible field values } } for smart-diff
  const latestDeviceListRef = useRef([]); // always-fresh device data for periodic flush
  const statusDataRef = useRef({}); // { deviceId: { everyFamilyAndAccountVisible: {...}, accountVisible: {...}, adminVisible: {...} } }
  const statusListenersRef = useRef({}); // { deviceId: [unsubscribeFn, ...] }
  const rootDataRef = useRef({}); // { deviceId: { ...raw root doc fields } } — fallback for pre-migration devices

  const { conversations, fetchConversations, getUnreadCountForAccount, getConversationStatus } = useAllConversations();

  // Unsent admin draft replies, keyed by accountUUID — drives the blue dot on
  // the message button. Independent of the red/green unread/open indicator:
  // both can be true at once and both must stay visible.
  const { drafts: adminDrafts, hasDraftForAccount } = useAllAdminDrafts();

  // Billing attention (red dot on the Billing button): one server-side sweep —
  // cancel requests + Stripe payment problems — fetched on load and refreshed
  // when the billing modal reports a change.
  const fetchBillingAttention = useCallback(async () => {
    try {
      if (!auth.currentUser) throw new Error('not signed in');
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/admin/billingAttention`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setBillingAttention(data.attention || {});
        // `partial` = the Stripe part of the sweep failed; cancel-request dots
        // are still reliable, payment-problem dots may be missing.
        setBillingAttentionDegraded(!!data.partial);
      } else {
        setBillingAttentionDegraded(true);
      }
    } catch (e) {
      console.warn('billingAttention fetch failed:', e.message);
      setBillingAttentionDegraded(true); // keep last-good dots, but flag staleness
    }
  }, []);

  useEffect(() => { fetchBillingAttention(); }, [fetchBillingAttention]);

  // A billing change made in the modal (subscription set up, cancellation
  // recorded or cleared) rewrites fields the Accounts table renders directly —
  // the Billing view's whole Status/Set up/Started/Amount block comes from the
  // account doc. Re-read just that one document so the row updates on close
  // instead of going stale until the tab is remounted.
  const handleBillingChanged = useCallback(async (accountUUID) => {
    fetchBillingAttention();
    const id = accountUUID || billingCheckTarget?.id;
    if (!id) return;
    try {
      const db = getFirestore(app);
      const snap = await getDoc(doc(db, 'accountsInfo', id));
      if (!snap.exists()) return;
      const fresh = { id: snap.id, ...snap.data() };
      setUsers(prev => prev.map(u => (u.id === id ? fresh : u)));
    } catch (e) {
      // Non-fatal: the dots still refreshed, the row is just stale until the
      // next accounts load.
      console.warn('Failed to refresh account after a billing change:', e.message);
    }
  }, [fetchBillingAttention, billingCheckTarget]);

  // Usage analytics (Accounts tab): compact per-family multi-person call
  // summaries written by the /admin/updateUsageAnalytics sweep. One-off read;
  // re-read after each button-triggered run.
  const fetchUsageAnalytics = useCallback(async () => {
    try {
      const db = getFirestore(app);
      const snap = await getDocs(collection(db, 'usageAnalytics'));
      const byFamily = {};
      let meta = null;
      snap.docs.forEach(d => {
        if (d.id === '_meta') meta = d.data();
        else byFamily[d.id] = d.data();
      });
      setUsageAnalytics(byFamily);
      setAnalyticsMeta(meta);
      setAnalyticsFetchDegraded(false);
    } catch (e) {
      // Keep last-good data; flag so the header doesn't claim "no analysis yet".
      console.warn('usageAnalytics fetch failed:', e.message);
      setAnalyticsFetchDegraded(true);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'accounts') return;
    fetchUsageAnalytics();
  }, [activeTab, fetchUsageAnalytics]);

  // "Update analysis" button: run the server sweep, then re-read the results.
  const handleUpdateAnalysis = async () => {
    setAnalyticsRunning(true);
    setAnalyticsError(false);
    try {
      if (!auth.currentUser) throw new Error('not signed in');
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/admin/updateUsageAnalytics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || `HTTP ${response.status}`);
      await fetchUsageAnalytics();
      // The daily rollup lives in its own collection, so it needs its own
      // re-read — otherwise a backfill press appears to do nothing. Pressed
      // from another view, mark it stale so the next visit to History picks the
      // new data up rather than showing the pre-sweep snapshot.
      if (historyViewActive) await refreshDailyStates();
      else invalidateDailyStates();
    } catch (e) {
      console.warn('updateUsageAnalytics failed:', e.message);
      setAnalyticsError(true); // keep last-good data on screen
    } finally {
      setAnalyticsRunning(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Switching view mode: if the current sort column isn't shown in the new
  // mode, fall back to email so the table isn't sorted by an invisible column.
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    const fieldsByMode = {
      default: ['batch', 'country', 'type', 'status'],
      usage: ['tier', 'lastCall', 'calls3mo'],
      billing: ['tier', 'setUpAt', 'startDate', 'billingState'],
      history: ['tier', 'ordered', 'delivered', 'lastCall'],
    };
    // Everything that isn't shown in the mode being switched to.
    const shown = new Set(['email', 'name', 'families', ...(fieldsByMode[mode] || [])]);
    const hidden = Object.values(fieldsByMode).flat().filter((f) => !shown.has(f));
    if (hidden.includes(sortField)) {
      setSortField('email');
      setSortDirection('asc');
    }
  };

  // Convert Firestore Timestamp or epoch ms to milliseconds
  const toMs = (ts) => {
    if (!ts) return 0;
    if (ts.toMillis) return ts.toMillis();
    if (typeof ts === 'number') return ts;
    if (ts.seconds) return ts.seconds * 1000;
    return 0;
  };

  // Get account setup completion time from setupCompletedAt field
  const getAccountSetupTime = (account) => {
    if (account.setupCompletedAt) return toMs(account.setupCompletedAt);
    return null;
  };

  // Get most recent first-sign-in time across all families in an account
  const getAccountLatestSignin = (account) => {
    const families = account.families || [];
    let latest = null;
    families.forEach(fid => {
      const t = firstSigninTimes[fid];
      if (t && (latest === null || t > latest)) latest = t;
    });
    return latest;
  };

  // Per-account usage rollup for the Usage view + tier filter: tier, deduped
  // call list across the account's families (see accountCalls), most recent
  // call, and 3-month call count.
  const accountUsage = useMemo(() => {
    const nowMs = Date.now();
    const windowStartMs = nowMs - 91 * 24 * 3600 * 1000;
    const out = {};
    users.forEach(u => {
      const calls = accountCalls(u, usageAnalytics);
      const lastCall = calls[0] || null; // calls are newest-first
      out[u.id] = {
        tier: deriveTier(u, usageAnalytics, signedInCounts[u.id], nowMs),
        calls,
        lastCall,
        // Sort/display key: falls back to the all-time summary field for
        // accounts whose retained calls were truncated by the sweep's
        // MAX_RETAINED_CALLS doc-size cap (those have a T1/T2 tier but may
        // have no call record for the date the summary reports).
        lastCallMs: lastCall ? lastCall.startMs : accountLastMultiPersonCallMs(u, usageAnalytics),
        calls3mo: calls.filter(c => c.startMs >= windowStartMs).length,
        // One device-status strip per family (offline/asleep/home/igloo),
        // rendered above the call bars in the Usage timeline.
        familyStatuses: (u.families || []).map(fid => ({
          familyId: fid,
          intervals: usageAnalytics?.[fid]?.statusIntervals || [],
        })),
      };
    });
    return out;
  }, [users, usageAnalytics, signedInCounts]);

  // Per-account billing record for the Billing view + its sort columns.
  // Firestore-only — see billingRecord.js; nothing here calls Stripe.
  const accountBilling = useMemo(() => {
    const nowMs = Date.now();
    const out = {};
    users.forEach(u => { out[u.id] = deriveBillingRecord(u, nowMs); });
    return out;
  }, [users]);

  // The all-time daily device-state rollup lives in its own collection and is
  // only read while the History view is on screen — see useAccountDailyStates
  // for why it is not on the usageAnalytics document.
  const historyViewActive = activeTab === 'accounts' && viewMode === 'history';
  const {
    dailyStates: dailyAnalytics,
    loading: dailyAnalyticsLoading,
    error: dailyAnalyticsError,
    refresh: refreshDailyStates,
    invalidate: invalidateDailyStates,
  } = useAccountDailyStates(historyViewActive);

  // Per-account journey rollup for the History view: milestones (order →
  // shipped → delivered → set up → first sign-in → first use → first call →
  // subscription), the per-family daily device-state records, and the oldest
  // thing we know about the account (which anchors the "All" range).
  // Calls are shared with accountUsage — same deduped list, no second derivation.
  const accountHistory = useMemo(() => {
    const out = {};
    users.forEach(u => {
      const milestones = accountMilestones(u, firstSigninTimes, usageAnalytics);
      const dailyStates = accountDailyStates(u, dailyAnalytics);
      const calls = accountUsage[u.id]?.calls || [];
      out[u.id] = {
        milestones,
        dailyStates,
        calls,
        earliestMs: accountEarliestMs(milestones, dailyStates, calls),
        orderedMs: toHistoryMs(u.prepaymentDate),
        // Taken from the milestone rather than re-derived, so the column and
        // the glyph on the timeline can never disagree about which unit's
        // delivery date counts.
        delivered: milestones.find(m => m.key === 'delivered') || null,
      };
    });
    return out;
  }, [users, firstSigninTimes, usageAnalytics, dailyAnalytics, accountUsage]);

  const sortedUsers = [...users].sort((a, b) => {
    let aVal, bVal;
    switch (sortField) {
      case 'email': aVal = a.email || ''; bVal = b.email || ''; break;
      case 'id': aVal = a.id || ''; bVal = b.id || ''; break;
      case 'name': aVal = a.nameAtOrder || ''; bVal = b.nameAtOrder || ''; break;
      case 'batch': aVal = a.batch || ''; bVal = b.batch || ''; break;
      case 'country': aVal = a.shippingCountryAtOrder || ''; bVal = b.shippingCountryAtOrder || ''; break;
      case 'type': aVal = a.accountType || ''; bVal = b.accountType || ''; break;
      case 'families': aVal = a.numOfFamilies ?? 0; bVal = b.numOfFamilies ?? 0; break;
      case 'tier': aVal = accountUsage[a.id]?.tier ?? 5; bVal = accountUsage[b.id]?.tier ?? 5; break;
      case 'lastCall': aVal = accountUsage[a.id]?.lastCallMs ?? 0; bVal = accountUsage[b.id]?.lastCallMs ?? 0; break;
      case 'calls3mo': aVal = accountUsage[a.id]?.calls3mo ?? 0; bVal = accountUsage[b.id]?.calls3mo ?? 0; break;
      // History view: order date. Unset sorts as 0 so never-ordered accounts
      // group at one end rather than scattering.
      case 'ordered': aVal = toHistoryMs(a.prepaymentDate) ?? 0; bVal = toHistoryMs(b.prepaymentDate) ?? 0; break;
      case 'delivered': aVal = accountHistory[a.id]?.delivered?.ms ?? 0; bVal = accountHistory[b.id]?.delivered?.ms ?? 0; break;
      // Billing view columns. Unset dates sort as 0 so never-set-up accounts
      // group together at one end rather than scattering.
      case 'setUpAt': aVal = accountBilling[a.id]?.setUpAtMs ?? 0; bVal = accountBilling[b.id]?.setUpAtMs ?? 0; break;
      case 'startDate': aVal = accountBilling[a.id]?.startMs ?? 0; bVal = accountBilling[b.id]?.startMs ?? 0; break;
      case 'billingState':
        aVal = billingStateOrder(accountBilling[a.id]?.state);
        bVal = billingStateOrder(accountBilling[b.id]?.state);
        break;
      case 'status': {
        // Primary: setupCompleted before accountCreated (when ascending)
        const aIsSetup = a.accountStatus === 'setupCompleted' ? 1 : 0;
        const bIsSetup = b.accountStatus === 'setupCompleted' ? 1 : 0;
        if (aIsSetup !== bIsSetup) {
          return sortDirection === 'asc' ? bIsSetup - aIsSetup : aIsSetup - bIsSetup;
        }
        // Secondary: within setupCompleted, sort by recency (most recent first)
        if (aIsSetup && bIsSetup) {
          const aTime = getAccountSetupTime(a) || 0;
          const bTime = getAccountSetupTime(b) || 0;
          if (aTime !== bTime) return sortDirection === 'asc' ? bTime - aTime : aTime - bTime;
        }
        // Fallback: alphabetical by email
        return (a.email || '').localeCompare(b.email || '');
      }
      default: aVal = ''; bVal = '';
    }
    if (sortField === 'status') return 0; // already handled above
    if (typeof aVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const cmp = String(aVal).localeCompare(String(bVal));
    return sortDirection === 'asc' ? cmp : -cmp;
  });


  // Filter sorted users by tier chips (empty selection = all), then by the
  // account-type tick menu (empty selection = all). Both apply to every view.
  const activeTierFilters = TIER_NUMBERS.filter(t => tierFilter[t]);
  const displayedUsers = sortedUsers
    .filter(u => activeTierFilters.length === 0 || activeTierFilters.includes(accountUsage[u.id]?.tier))
    .filter(u => isAccountTypeVisible(u, typeFilter));

  // Per-tier counts for the filter chips (after batch/shipped filters would be
  // confusing — count over all accounts so the chips are stable).
  const tierCounts = useMemo(() => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    users.forEach(u => { counts[accountUsage[u.id]?.tier ?? 5]++; });
    return counts;
  }, [users, accountUsage]);

  // Per-type counts for the type menu — over all accounts, same reasoning.
  const typeCounts = useMemo(() => {
    const counts = {};
    users.forEach(u => {
      const key = accountTypeKey(u.accountType);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [users]);

  const allTypeKeys = useMemo(
    () => [...ACCOUNT_TYPES.map(t => t.key), OTHER_ACCOUNT_TYPE],
    []
  );

  // Comments for the inline comment column, shown in both the Billing and
  // History views. Only loaded while one of them is on screen (one small read
  // per displayed account).
  const billingViewActive = activeTab === 'accounts' && viewMode === 'billing';
  const commentsViewActive = billingViewActive || historyViewActive;
  // Keyed on the SET of displayed ids, not their display order: re-sorting the
  // table must not refire the per-account comment fan-out, only a genuine
  // change of membership (a filter toggle, or the accounts list reloading).
  const displayedUserIdsKey = [...new Set(displayedUsers.map(u => u.id))].sort().join(',');
  const displayedUserIds = useMemo(
    () => (displayedUserIdsKey ? displayedUserIdsKey.split(',') : []),
    [displayedUserIdsKey]
  );
  const {
    comments: accountComments,
    loading: accountCommentsLoading,
    error: accountCommentsError,
    unavailable: accountCommentsUnavailable,
    failedIds: accountCommentFailedIds,
    refreshOne: refreshAccountComment,
  } = useAccountComments(commentsViewActive, displayedUserIds);

  // Support-message + admin-note activity for the History view's message lane.
  // Same gating and fan-out shape as the comments above.
  const {
    activity: messageActivity,
    loading: messageActivityLoading,
    error: messageActivityError,
    unavailable: messageActivityUnavailable,
    failedIds: messageActivityFailedIds,
  } = useAccountMessageActivity(historyViewActive, displayedUserIds);

  // "Now" for the History axis, rounded to the quarter hour and re-checked on
  // a timer. An admin dashboard is left open for hours; without this the axis
  // end freezes where it was on first render and activity after that point is
  // silently filtered out of every row. Rounding keeps the memo key stable so
  // the table is not rebuilt every tick.
  const [historyNowMs, setHistoryNowMs] = useState(() => roundToQuarterHour(Date.now()));
  useEffect(() => {
    if (!historyViewActive) return undefined;
    // Catch up the moment the view opens, not only on the next tick: the
    // dashboard may have sat on another tab for hours, and until this runs the
    // axis would end where it did at mount and filter out everything since.
    setHistoryNowMs(roundToQuarterHour(Date.now()));
    const id = setInterval(() => setHistoryNowMs(roundToQuarterHour(Date.now())), 60_000);
    return () => clearInterval(id);
  }, [historyViewActive]);

  // The ONE window every History row is drawn against. Computed here, never
  // per row — rows sharing an axis is what makes the view readable vertically.
  const historyWindow = useMemo(() => {
    let earliest = null;
    for (const u of displayedUsers) {
      const t = accountHistory[u.id]?.earliestMs;
      if (t != null && (earliest === null || t < earliest)) earliest = t;
    }
    return resolveHistoryWindow(historyRange, earliest, historyNowMs);
    // displayedUserIdsKey stands in for displayedUsers: a re-sort must not
    // recompute the window, only a change of membership.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyRange, accountHistory, displayedUserIdsKey, historyNowMs]);

  // A rejected custom range falls back to the default window. Say so, rather
  // than leaving the month inputs asserting a range the chart isn't drawing.
  const historyRangeInvalid = !isCustomRangeValid(historyRange);

  const SortHeader = ({ field, children, align = 'left' }) => (
    <th
      className={`text-${align} py-1.5 px-2 font-medium text-sm cursor-pointer hover:bg-muted/50 select-none`}
      onClick={() => handleSort(field)}
    >
      <div className={`flex items-center gap-1 ${align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : ''}`}>
        {children}
        {sortField === field ? (
          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-30" />
        )}
      </div>
    </th>
  );

  const handleOpenChat = (user) => {
    setSelectedChatUser(user);
    setShowSupportChat(true);
  };

  const handleCloseChat = () => {
    setShowSupportChat(false);
    setSelectedChatUser(null);
    fetchConversations();
  };

  // Per-family alert unread counts.
  //
  // Same pattern as supportConversations for messaging: AlertWriter on the
  // device maintains an `unreadCount` field on the per-family parent doc
  // criticalAlerts/{familyId} via FieldValue.increment(1) on each fire;
  // this webapp adjusts it by ±1 on mark-read / mark-unread / archive.
  //
  // Self-heal: an out-of-band counter (e.g. <0 from a past drift bug) hides
  // the family from every badge in the UI. When we see a non-positive count
  // we re-derive the truth from the subcollection via getCountFromServer and
  // patch the parent doc. Deduped per-session via repairedRef so each suspect
  // family pays at most one count query per page load.
  const repairedRef = useRef(new Set());
  useEffect(() => {
    const db = getFirestore(app);
    const unsubscribe = onSnapshot(
      collection(db, 'criticalAlerts'),
      (snapshot) => {
        const next = {};
        snapshot.docs.forEach((d) => {
          const count = d.data()?.unreadCount ?? 0;
          if (count > 0) next[d.id] = count;
          if (count <= 0 && !repairedRef.current.has(d.id)) {
            repairedRef.current.add(d.id);
            (async () => {
              try {
                const agg = await getCountFromServer(
                  query(collection(db, 'criticalAlerts', d.id, 'alerts'), where('readAt', '==', null))
                );
                const trueCount = agg.data().count;
                if (trueCount !== count) {
                  console.warn(`AdminDashboard: repairing unreadCount for ${d.id}: ${count} -> ${trueCount}`);
                  await setDoc(doc(db, 'criticalAlerts', d.id), { unreadCount: trueCount }, { merge: true });
                }
              } catch (e) {
                console.error(`AdminDashboard: failed to repair unreadCount for ${d.id}`, e);
              }
            })();
          }
        });
        setAlertUnreadByFamily(next);
      },
      (error) => {
        console.error('AdminDashboard: criticalAlerts listener error:', error);
      }
    );
    return () => unsubscribe();
  }, []);

  // Track which families have an active hotplug doc (written by device on sign-in, deleted on sign-out).
  // Used to enable/disable the per-device hotplug button.
  useEffect(() => {
    const db = getFirestore(app);
    const unsubscribe = onSnapshot(collectionGroup(db, 'hotplug'), (snapshot) => {
      const families = new Set();
      snapshot.docs.forEach(d => {
        // Path: deviceManagement/{familyID}/hotplug/{docId}
        const familyID = d.ref.parent.parent?.id;
        if (familyID) families.add(familyID);
      });
      setHotplugFamilies(families);
    }, (error) => {
      console.error('AdminDashboard: hotplug collectionGroup listener error:', error);
    });
    return () => unsubscribe();
  }, []);

  // Track which families have active override settings.
  // Used to visually indicate the per-device override button.
  useEffect(() => {
    const db = getFirestore(app);
    const unsubscribe = onSnapshot(collectionGroup(db, 'overrideSettings'), (snapshot) => {
      // Two kinds of overrideSettings docs share this collection-group:
      //   deviceManagement/{familyID}/overrideSettings/{category} — family tier
      //   iglooInfo/{iglooID}/overrideSettings/{category}         — igloo tier
      // Split by the parent doc's collection so family override indicators
      // never pick up igloo docs (and vice versa).
      const families = new Set();
      const iglooIds = new Set();
      snapshot.docs.forEach(d => {
        const parentDoc = d.ref.parent.parent;
        const rootCollection = parentDoc?.parent?.id;
        if (rootCollection === 'deviceManagement') {
          if (parentDoc.id && Object.keys(d.data()).length > 0) families.add(parentDoc.id);
        } else if (rootCollection === 'iglooInfo') {
          if (parentDoc.id && Object.keys(d.data()).length > 0) iglooIds.add(parentDoc.id);
        }
      });
      setOverrideFamilies(families);
      setOverrideIgloos(iglooIds);
    }, (error) => {
      console.error('AdminDashboard: overrideSettings collectionGroup listener error:', error);
    });
    return () => unsubscribe();
  }, []);

  // NOTE: the dashboard deliberately reads NO individual igloo setting. It once
  // watched both global igloo-settings visibility docs plus the per-igloo override
  // tier to render a videoBackend badge on each igloo row; that was removed
  // (2026-08-07) because the dashboard should not single out one setting — settings
  // are read and written through the generic settings editors, and a value only
  // matters to whoever opens one. Anything needing a specific setting here would be
  // reintroducing that coupling.

  useEffect(() => {
    if (activeTab !== 'devices') return;

    const db = getFirestore(app);

    // Fetch accounts for grouping devices, and fetch family names for all families
    const fetchAccounts = async () => {
      const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
      const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      accountsList.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
      setDeviceAccounts(accountsList);

      // Collect all unique family IDs across all accounts and fetch their names + sign-in times
      const allFamilyIds = new Set();
      accountsList.forEach(account => {
        (account.families || []).forEach(fid => allFamilyIds.add(fid));
      });
      const nameMap = {};
      const signinTimes = {};
      await Promise.all(
        Array.from(allFamilyIds).map(async (familyId) => {
          try {
            const familySnap = await getDoc(doc(db, 'familyInfo', familyId));
            if (familySnap.exists()) {
              const data = familySnap.data();
              nameMap[familyId] = data.name || familyId;
              if (data.firstSignin) signinTimes[familyId] = toMs(data.firstSignin);
            } else {
              nameMap[familyId] = familyId;
            }
          } catch (e) {
            nameMap[familyId] = familyId;
          }
        })
      );
      setAllFamilyNames(nameMap);
      allFamilyNamesRef.current = nameMap;
      setFirstSigninTimes(prev => ({ ...prev, ...signinTimes }));
    };
    fetchAccounts();

    // Smart-diff listener: only triggers React re-render when user-visible fields change.
    // Raw pings that don't change online/offline status are silently absorbed.
    const ONLINE_THRESHOLD_MS = 35 * 1000; // 30s ping period + 5s buffer

    const getVisibleSnapshot = (device) => {
      const status = statusDataRef.current[device.id] || {};
      const famAccVis = status.everyFamilyAndAccountVisible || {};
      const accVis = status.accountVisible || {};
      const adminVis = status.adminVisible || {};
      const root = rootDataRef.current[device.id] || {};

      const onlinePing = famAccVis.onlinePing ?? root.onlinePing;
      const pingMs = onlinePing?.toMillis?.() ?? 0;
      return {
        isOnline: pingMs > 0 && (Date.now() - pingMs < ONLINE_THRESHOLD_MS),
        currentAppVersion: accVis.currentAppVersion ?? root.currentAppVersion ?? '',
        targetAppVersion: accVis.targetAppVersion ?? root.targetAppVersion ?? '',
        instantUpdate: !!(accVis.instantUpdate ?? root.instantUpdate),
        otaStatus: adminVis.otaStatus ?? root.otaStatus ?? '',
        lastUploadedLogMs: (adminVis.lastUploadedLog ?? root.lastUploadedLog)?.toMillis?.() ?? 0,
        currentOsVersion: adminVis.currentOsVersion ?? root.currentOsVersion ?? '',
        targetOsVersion: adminVis.targetOsVersion ?? root.targetOsVersion ?? '',
        instantOsUpdate: !!(adminVis.instantOsUpdate ?? root.instantOsUpdate),
        osOtaStatus: adminVis.osOtaStatus ?? root.osOtaStatus ?? '',
      };
    };

    const unsubscribe = onSnapshot(collection(db, 'deviceManagement'), async (snapshot) => {
      const baseDeviceList = snapshot.docs.map(d => ({ id: d.id }));

      // Cache raw root data for fallback on pre-migration devices
      for (const d of snapshot.docs) {
        rootDataRef.current[d.id] = d.data();
      }

      // Use cached family names; only fetch for genuinely unknown devices
      const cachedNames = allFamilyNamesRef.current;
      const unknownIds = baseDeviceList
        .map(d => d.id)
        .filter(id => !(id in cachedNames));
      if (unknownIds.length > 0) {
        await Promise.all(
          unknownIds.map(async (id) => {
            try {
              const familySnap = await getDoc(doc(db, 'familyInfo', id));
              cachedNames[id] = familySnap.exists() ? familySnap.data().name : id;
            } catch (e) {
              cachedNames[id] = id;
            }
          })
        );
        allFamilyNamesRef.current = { ...cachedNames };
      }
      const deviceList = baseDeviceList.map(device => {
        const status = statusDataRef.current[device.id] || {};
        const famAccVis = status.everyFamilyAndAccountVisible || {};
        const accVis = status.accountVisible || {};
        const adminVis = status.adminVisible || {};
        const root = rootDataRef.current[device.id] || {};
        return {
          id: device.id,
          familyName: cachedNames[device.id] || device.id,
          onlinePing: famAccVis.onlinePing ?? root.onlinePing,
          currentAppVersion: accVis.currentAppVersion ?? root.currentAppVersion,
          targetAppVersion: accVis.targetAppVersion ?? root.targetAppVersion,
          instantUpdate: accVis.instantUpdate ?? root.instantUpdate,
          otaStatus: adminVis.otaStatus ?? root.otaStatus,
          lastUploadedLog: adminVis.lastUploadedLog ?? root.lastUploadedLog,
          logDumpStatus: adminVis.logDumpStatus,
          logDumpTimestamp: adminVis.logDumpTimestamp,
          logDumpPath: adminVis.logDumpPath,
          crashLogPath: adminVis.crashLogPath,
          currentOsVersion: adminVis.currentOsVersion ?? root.currentOsVersion,
          targetOsVersion: adminVis.targetOsVersion ?? root.targetOsVersion,
          instantOsUpdate: adminVis.instantOsUpdate ?? root.instantOsUpdate,
          osOtaStatus: adminVis.osOtaStatus ?? root.osOtaStatus,
        };
      });

      // Check if anything the user can see has actually changed
      const oldVisible = deviceVisibleRef.current;
      let changed = deviceList.length !== Object.keys(oldVisible).length;
      if (!changed) {
        for (const device of deviceList) {
          const newVis = getVisibleSnapshot(device);
          const oldVis = oldVisible[device.id];
          if (!oldVis) { changed = true; break; }
          for (const key of Object.keys(newVis)) {
            if (newVis[key] !== oldVis[key]) { changed = true; break; }
          }
          if (changed) break;
        }
      }

      // Always update the visible snapshot ref (cheap)
      const newVisible = {};
      for (const device of deviceList) {
        newVisible[device.id] = getVisibleSnapshot(device);
      }
      deviceVisibleRef.current = newVisible;

      // Always keep the ref fresh (cheap, no re-render)
      latestDeviceListRef.current = deviceList;

      // Only trigger a React re-render when something visible changed
      if (changed) {
        setDevices(deviceList);
      }
    });

    return () => unsubscribe();
  }, [activeTab]);

  // Listeners for status subcollection docs (everyFamilyAndAccountVisible, accountVisible, adminVisible)
  useEffect(() => {
    if (activeTab !== 'devices') {
      Object.values(statusListenersRef.current).forEach(unsubs => unsubs.forEach(u => u()));
      statusListenersRef.current = {};
      return;
    }
    if (devices.length === 0) return;

    const db = getFirestore(app);
    const currentIds = new Set(devices.map(d => d.id));
    const trackedIds = new Set(Object.keys(statusListenersRef.current));

    for (const id of trackedIds) {
      if (!currentIds.has(id)) {
        statusListenersRef.current[id].forEach(u => u());
        delete statusListenersRef.current[id];
        delete statusDataRef.current[id];
      }
    }

    for (const id of currentIds) {
      if (!trackedIds.has(id)) {
        const unsubs = [];
        const statusDocs = ['everyFamilyAndAccountVisible', 'accountVisible', 'adminVisible'];
        for (const statusDoc of statusDocs) {
          const ref = doc(db, 'deviceManagement', id, 'status', statusDoc);
          const unsub = onSnapshot(ref, (snap) => {
            if (!statusDataRef.current[id]) statusDataRef.current[id] = {};
            statusDataRef.current[id][statusDoc] = snap.exists() ? snap.data() : {};

            if (statusDoc === 'accountVisible') {
              const rssi = snap.exists() && typeof snap.data().rssi === 'number' ? snap.data().rssi : null;
              setDeviceRssi(prev => prev[id] === rssi ? prev : { ...prev, [id]: rssi });
              const location = snap.exists() && typeof snap.data().location === 'string' ? snap.data().location : null;
              setDeviceLocation(prev => prev[id] === location ? prev : { ...prev, [id]: location });
            }

            const latest = latestDeviceListRef.current;
            if (latest.length > 0) {
              const status = statusDataRef.current[id] || {};
              const famAccVis = status.everyFamilyAndAccountVisible || {};
              const accVis = status.accountVisible || {};
              const adminVis = status.adminVisible || {};
              const root = rootDataRef.current[id] || {};
              const updatedList = latest.map(device => {
                if (device.id !== id) return device;
                return {
                  ...device,
                  onlinePing: famAccVis.onlinePing,
                  currentAppVersion: accVis.currentAppVersion,
                  targetAppVersion: accVis.targetAppVersion ?? root.targetAppVersion,
                  instantUpdate: accVis.instantUpdate ?? root.instantUpdate,
                  otaStatus: adminVis.otaStatus,
                  lastUploadedLog: adminVis.lastUploadedLog,
                  logDumpStatus: adminVis.logDumpStatus,
                  logDumpTimestamp: adminVis.logDumpTimestamp,
                  logDumpPath: adminVis.logDumpPath,
                  crashLogPath: adminVis.crashLogPath,
                  currentOsVersion: adminVis.currentOsVersion,
                  targetOsVersion: adminVis.targetOsVersion ?? root.targetOsVersion,
                  instantOsUpdate: adminVis.instantOsUpdate ?? root.instantOsUpdate,
                  osOtaStatus: adminVis.osOtaStatus,
                };
              });
              latestDeviceListRef.current = updatedList;
              setDevices(updatedList);
            }
          });
          unsubs.push(unsub);
        }
        statusListenersRef.current[id] = unsubs;
      }
    }
  }, [devices, activeTab]);

  // Periodic flush: keeps devices state fresh so onlinePing doesn't go stale.
  // Without this, skipped setDevices() calls leave stale onlinePing in state,
  // causing devices to appear offline when other state changes trigger re-renders.
  useEffect(() => {
    if (activeTab !== 'devices') return;
    const interval = setInterval(() => {
      if (latestDeviceListRef.current.length > 0) {
        setDevices(latestDeviceListRef.current);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'devices') return;

    const db = getFirestore(app);
    const q = query(
      collection(db, 'appUpdates'),
      where('status', '==', 'available'),
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.filter(doc => !isNaN(parseInt(doc.id, 10)));
      docs.sort((a, b) => {
        const timeA = a.data().timestamp?.toMillis?.() ?? 0;
        const timeB = b.data().timestamp?.toMillis?.() ?? 0;
        return timeB - timeA;
      });
      setAvailableAppVersions(docs);
    });

    return () => unsubscribe();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'devices') return;
    
    const db = getFirestore(app);
    const q = query(
      collection(db, 'osUpdates'),
      where('status', '==', 'available'),
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.filter(doc => !isNaN(parseInt(doc.id, 10)));
      docs.sort((a, b) => {
        const timeA = a.data().timestamp?.toMillis?.() ?? 0;
        const timeB = b.data().timestamp?.toMillis?.() ?? 0;
        return timeB - timeA;
      });
      setAvailableOsVersions(docs);
    });

    return () => unsubscribe();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'accounts') return;

    const db = getFirestore(app);

    const fetchAccounts = async () => {
      const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
      const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(accountsList);

      // Check which families have signed in and fetch first sign-in times
      const counts = {};
      const signinTimes = {};
      await Promise.all(
        accountsList.map(async (account) => {
          const families = account.families || [];
          if (families.length === 0) { counts[account.id] = 0; return; }
          let signedIn = 0;
          await Promise.all(
            families.map(async (familyId) => {
              try {
                const statusSnap = await getDoc(doc(db, 'deviceManagement', familyId, 'status', 'accountVisible'));
                if (statusSnap.exists() && statusSnap.data().currentAppVersion) {
                  signedIn++;
                }
                // Read firstSignin from familyInfo
                const familySnap = await getDoc(doc(db, 'familyInfo', familyId));
                if (familySnap.exists() && familySnap.data().firstSignin) {
                  signinTimes[familyId] = toMs(familySnap.data().firstSignin);
                }
              } catch (e) { /* ignore */ }
            })
          );
          counts[account.id] = signedIn;
        })
      );
      setSignedInCounts(counts);
      setFirstSigninTimes(signinTimes);
    };

    fetchAccounts();
  }, [activeTab]);

  // Check for openConversation query parameter (from email notification links)
  useEffect(() => {
    const conversationUUID = searchParams.get('openConversation');

    if (conversationUUID && users.length > 0 && !showSupportChat) {
      // Find the user with this accountUUID
      const user = users.find(u => u.id === conversationUUID);
      if (user) {
        handleOpenChat(user);
        // Clean up the URL without refreshing the page — only this param, and
        // through the router so its location state stays in sync
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.delete('openConversation');
          return next;
        }, { replace: true });
      }
    }
  }, [users, showSupportChat, searchParams, setSearchParams]);

  useEffect(() => {
    if (activeTab !== 'igloos') return;
    
    const db = getFirestore(app);
    const unsubscribe = onSnapshot(collection(db, 'iglooInfo'), async (snapshot) => {
      const baseIglooList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const iglooList = await Promise.all(
        baseIglooList.map(async (igloo) => {
          try {
            // Get the first controller (familyID) from the controllers array
            const controllerFamilyId = igloo.controllers?.[0];
            if (!controllerFamilyId) {
              return { ...igloo, iglooName: igloo.id, controllerName: 'No Controller' };
            }
            
            // Get the controller family name
            const familySnap = await getDoc(doc(db, 'familyInfo', controllerFamilyId));
            const controllerName = familySnap.exists() ? familySnap.data().name : controllerFamilyId;
            
            // Get the igloo name from the family's igloos collection
            const iglooSnap = await getDoc(doc(db, 'familyInfo', controllerFamilyId, 'igloos', igloo.id));
            const iglooName = iglooSnap.exists() ? iglooSnap.data().name : igloo.id;
            
            // Get member family names
            const memberFamilies = [];
            if (igloo.members && Array.isArray(igloo.members)) {
              for (const memberId of igloo.members) {
                try {
                  const memberSnap = await getDoc(doc(db, 'familyInfo', memberId));
                  const memberName = memberSnap.exists() ? memberSnap.data().name : memberId;
                  memberFamilies.push({ id: memberId, name: memberName });
                } catch (e) {
                  console.error(`Could not fetch member family info for ${memberId}`, e);
                  memberFamilies.push({ id: memberId, name: memberId });
                }
              }
            }
            
            // Combine controller and members into allFamilies for settings
            const allFamilies = [
              { id: controllerFamilyId, name: controllerName, isController: true },
              ...memberFamilies.map(member => ({ ...member, isController: false }))
            ];
            
            return { ...igloo, iglooName, controllerName, controllerFamilyId, memberFamilies, allFamilies };
          } catch (e) {
            console.error(`Could not fetch info for igloo ${igloo.id}`, e);
            return { ...igloo, iglooName: igloo.id, controllerName: 'Error loading', controllerFamilyId: null };
          }
        })
      );
      
      // Sort igloos alphabetically by igloo name, then by controller name
      const sortedIglooList = iglooList.sort((a, b) => {
        const iglooNameComparison = a.iglooName.localeCompare(b.iglooName);
        if (iglooNameComparison !== 0) {
          return iglooNameComparison;
        }
        return a.controllerName.localeCompare(b.controllerName);
      });
      
      setIgloos(sortedIglooList);
      setIgloosLoaded(true);
    });

    return () => unsubscribe();
  }, [activeTab]);

  // Track live sessions per igloo so the igloos tab can show a "live" badge,
  // enable the stats overlay, and warn before flipping an occupied igloo.
  useEffect(() => {
    if (activeTab !== 'igloos') return;
    const db = getFirestore(app);
    const unsub = onSnapshot(
      collection(db, 'iglooSessionsLive'),
      (snapshot) => {
        const counts = {};
        snapshot.docs.forEach((d) => {
          const data = d.data() || {};
          const participants = Array.isArray(data.participants) ? data.participants : [];
          counts[d.id] = participants.filter((p) => {
            const iv = p.intervals && p.intervals[p.intervals.length - 1];
            return iv && !iv.leftAt;
          }).length;
        });
        setLiveSessionsByIgloo(counts);
      },
      (e) => { console.error('AdminDashboard: iglooSessionsLive listener error', e); setLiveSessionsByIgloo({}); },
    );
    return () => unsub();
  }, [activeTab]);

  // Build familyId -> account (invert accountsInfo.families) so igloos group under
  // their controller family's account. Read once per igloos-tab visit.
  useEffect(() => {
    if (activeTab !== 'igloos') return;
    const db = getFirestore(app);
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'accountsInfo'));
        const map = {};
        snap.docs.forEach((d) => {
          const data = d.data() || {};
          (data.families || []).forEach((fid) => { map[fid] = { accountId: d.id, email: data.email }; });
        });
        setFamilyIdToAccount(map);
        setIglooAccountsError(false);
      } catch (e) {
        console.error('AdminDashboard: accountsInfo read for igloos failed', e);
        setIglooAccountsError(true);
      } finally {
        setIglooAccountsLoaded(true);
      }
    })();
  }, [activeTab]);

  // Group igloos under their controller family's account (+ an orphaned bucket for
  // igloos whose controller resolves to no account).
  const iglooAccountGroups = useMemo(() => {
    const byAccount = {}; // accountId -> { account, igloos }
    const orphaned = [];
    igloos.forEach((igloo) => {
      const controllerFid = igloo.controllers?.[0];
      const acc = controllerFid ? familyIdToAccount[controllerFid] : null;
      if (acc) {
        if (!byAccount[acc.accountId]) {
          byAccount[acc.accountId] = { account: { id: acc.accountId, email: acc.email }, igloos: [] };
        }
        byAccount[acc.accountId].igloos.push(igloo);
      } else {
        orphaned.push(igloo);
      }
    });
    const accounts = Object.values(byAccount);
    // Igloos within a group are name-sorted; account groups are ordered by email.
    accounts.forEach((g) => g.igloos.sort((a, b) =>
      (a.iglooName || '').localeCompare(b.iglooName || '')));
    orphaned.sort((a, b) => (a.iglooName || '').localeCompare(b.iglooName || ''));
    accounts.sort((a, b) =>
      (a.account.email || '').localeCompare(b.account.email || ''));
    return { accounts, orphaned };
  }, [igloos, familyIdToAccount]);

  // Remember the Devices list scroll position across the dashboard unmount
  // caused by visiting a device's remote-control page, so "Back to Devices"
  // (or browser back) lands where the user left off. Must be a LAYOUT effect
  // cleanup: it runs during the unmount commit, before the browser lays out
  // the (short) next page and clamps window.scrollY to 0.
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  useLayoutEffect(() => {
    return () => {
      if (activeTabRef.current === 'devices') {
        try {
          sessionStorage.setItem('higlo.admin.devicesScrollY', String(window.scrollY));
        } catch (error) { /* storage unavailable — skip */ }
      }
    };
  }, []);
  // Read (and consume) the saved position on mount; restore after the list
  // has rendered, retrying on data arrivals until the page is tall enough for
  // the target position. Consuming must happen in an effect (never in render:
  // StrictMode discards the first of its double renders, which would eat the
  // value) and only the first read may assign (StrictMode's simulated unmount
  // re-saves a stray 0 between effect replays).
  const pendingScrollRestoreRef = useRef(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('higlo.admin.devicesScrollY');
      if (raw != null) {
        sessionStorage.removeItem('higlo.admin.devicesScrollY');
        if (pendingScrollRestoreRef.current == null) {
          pendingScrollRestoreRef.current = Number(raw) || 0;
        }
      }
    } catch (error) { /* storage unavailable — skip */ }
  }, []);
  useEffect(() => {
    const y = pendingScrollRestoreRef.current;
    if (y == null) return;
    if (activeTab !== 'devices') {
      // Restoration only applies straight back onto the Devices tab.
      pendingScrollRestoreRef.current = null;
      return;
    }
    if (deviceAccounts.length === 0) return; // list not rendered yet
    // Explicit instant behavior: the app CSS sets scroll-behavior smooth on
    // <html>, which both animates and (in Chrome) can swallow plain scrollTo
    // calls entirely during render churn.
    window.scrollTo({ top: y, left: 0, behavior: 'instant' });
    if (document.documentElement.scrollHeight >= y + window.innerHeight) {
      pendingScrollRestoreRef.current = null; // fully restored
    }
  }, [activeTab, deviceAccounts, devices]);

  const handleTargetVersionChange = async (deviceId, newVersion) => {
    const db = getFirestore(app);
    const deviceRef = doc(db, 'deviceManagement', deviceId);
    const statusRef = doc(db, 'deviceManagement', deviceId, 'status', 'accountVisible');
    const value = parseInt(newVersion, 10);

    try {
      await Promise.all([
        setDoc(deviceRef, { targetAppVersion: value }, { merge: true }),
        setDoc(statusRef, { targetAppVersion: value }, { merge: true }),
      ]);
    } catch (error) {
      console.error(`Failed to update targetAppVersion for ${deviceId}:`, error);
    }
  };

  const handleInstantUpdateChange = async (deviceId, isChecked) => {
    const db = getFirestore(app);
    const deviceRef = doc(db, 'deviceManagement', deviceId);
    const statusRef = doc(db, 'deviceManagement', deviceId, 'status', 'accountVisible');

    try {
      await Promise.all([
        setDoc(deviceRef, { instantUpdate: isChecked }, { merge: true }),
        setDoc(statusRef, { instantUpdate: isChecked }, { merge: true }),
      ]);
    } catch (error) {
      console.error(`Failed to update instantUpdate for ${deviceId}:`, error);
    }
  };

  const handleTargetOsVersionChange = async (deviceId, newVersion) => {
    const db = getFirestore(app);
    const deviceRef = doc(db, 'deviceManagement', deviceId);
    const statusRef = doc(db, 'deviceManagement', deviceId, 'status', 'adminVisible');
    const value = parseInt(newVersion, 10);

    try {
      await Promise.all([
        setDoc(deviceRef, { targetOsVersion: value }, { merge: true }),
        setDoc(statusRef, { targetOsVersion: value }, { merge: true }),
      ]);
    } catch (error) {
      console.error(`Failed to update targetOsVersion for ${deviceId}:`, error);
    }
  };

  const handleInstantOsUpdateChange = async (deviceId, isChecked) => {
    const db = getFirestore(app);
    const deviceRef = doc(db, 'deviceManagement', deviceId);
    const statusRef = doc(db, 'deviceManagement', deviceId, 'status', 'adminVisible');

    try {
      await Promise.all([
        setDoc(deviceRef, { instantOsUpdate: isChecked }, { merge: true }),
        setDoc(statusRef, { instantOsUpdate: isChecked }, { merge: true }),
      ]);
    } catch (error) {
      console.error(`Failed to update instantOsUpdate for ${deviceId}:`, error);
    }
  };

  const handleRemoveUser = async (userEmail) => {
    setIsRemoving(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);

      const response = await fetch(`${API_BASE_URL}/admin/removeAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ email: userEmail }),
      });

      const data = await response.json();

      if (data.success) {
        setUsers(users.filter(user => user.email !== userEmail));
        setShowModal(false);
      } else {
        setErrorMessage(data.error || 'Failed to remove account');
      }
    } catch (error) {
      setErrorMessage('Error removing account: ' + error.message);
    } finally {
      setIsRemoving(false);
    }
  };

  const confirmRemoveUser = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const confirmResetUser = (user) => {
    setResetUser(user);
    setResetShipping(false);
    setShowResetModal(true);
  };

  const handleResetUser = async (userEmail) => {
    setIsResetting(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);

      const response = await fetch(`${API_BASE_URL}/admin/resetAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ email: userEmail, resetShipping }),
      });

      const data = await response.json();

      if (data.success) {
        // Reflect the rolled-back state locally without refetching.
        setUsers(users.map(u => u.email === userEmail ? {
          ...u,
          accountStatus: 'accountCreated',
          name: undefined,
          families: undefined,
          primaryFamilyID: undefined,
          numOfOwningFamilies: undefined,
          numOfInvitedFamilies: undefined,
          setupCompletedAt: undefined,
          ...(resetShipping ? {
            shippingAddresses: undefined,
            shippingAddressesConfirmed: undefined,
            preparingForShipping: undefined,
            shipped: undefined,
            shippedAt: undefined,
            trackingNumbers: undefined,
            deliveryDates: undefined,
          } : {}),
        } : u));
        setShowResetModal(false);
        setResetUser(null);
        setResetShipping(false);
      } else {
        setErrorMessage(data.error || 'Failed to reset account');
      }
    } catch (error) {
      setErrorMessage('Error resetting account: ' + error.message);
    } finally {
      setIsResetting(false);
    }
  };

  const generateOrderCode = () => {
    const prefix = 'BETA';
    const randomNumber = Math.floor(Math.random() * 10000000000).toString().padStart(10, '0');
    return `${prefix}${randomNumber}`;
  };

  const handleAddUser = async () => {
    if (!/\S+@\S+\.\S+/.test(newUserEmail)) {
      setErrorMessage("Invalid email format");
      return;
    }

    if (newNumFamilies <= 0) {
      setErrorMessage("Number of families must be greater than 0");
      return;
    }

    setIsAddingUser(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);

      const accountData = {
        email: newUserEmail,
        numOfFamilies: parseInt(newNumFamilies, 10),
        accountType: newAccountType,
        orderCode: generateOrderCode(),
      };
      if (newUserName.trim()) accountData.nameAtOrder = newUserName.trim();
      if (newBatch.trim()) accountData.batch = newBatch.trim();
      if (newCountry) accountData.shippingCountryAtOrder = newCountry;

      const response = await fetch(`${API_BASE_URL}/admin/addAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(accountData),
      });

      const data = await response.json();

      if (data.success) {
        // Refetch the accounts list to get the new account with its UUID
        const db = getFirestore(app);
        const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
        const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(accountsList);
        setNewUserEmail('');
        setNewUserName('');
        setNewNumFamilies(2);
        setNewAccountType('freeHearthOwningAccount');
        setNewBatch('');
        setNewCountry('');
        setErrorMessage('');
      } else {
        setErrorMessage(data.error || 'Failed to add account');
      }
    } catch (error) {
      setErrorMessage('Error adding account: ' + error.message);
    } finally {
      setIsAddingUser(false);
    }
  };

  const handleImportAccounts = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsImporting(true);
    setImportMessage('');
    setErrorMessage('');

    try {
      const fileContent = await file.text();
      let accounts = [];

      // Parse file based on type
      if (importFileType === 'json') {
        accounts = JSON.parse(fileContent);
      } else if (importFileType === 'csv') {
        // Parse CSV
        const lines = fileContent.split('\n').filter(line => line.trim());

        // Helper function to parse a CSV line (handles quoted values)
        const parseCSVLine = (line) => {
          const values = [];
          let currentValue = '';
          let insideQuotes = false;

          for (let i = 0; i < line.length; i++) {
            const char = line[i];

            if (char === '"') {
              if (insideQuotes && line[i + 1] === '"') {
                currentValue += '"';
                i++;
              } else {
                insideQuotes = !insideQuotes;
              }
            } else if (char === ',' && !insideQuotes) {
              values.push(currentValue.trim());
              currentValue = '';
            } else {
              currentValue += char;
            }
          }
          values.push(currentValue.trim());
          return values;
        };

        const headers = parseCSVLine(lines[0]);

        accounts = lines.slice(1).map(line => {
          const values = parseCSVLine(line);

          const account = {};
          headers.forEach((header, index) => {
            let value = values[index] || '';

            // Skip type detection for empty values
            if (value === '') {
              account[header] = value;
              return;
            }

            // Try to parse JSON objects/arrays
            if ((value.startsWith('{') && value.endsWith('}')) ||
                (value.startsWith('[') && value.endsWith(']'))) {
              try {
                value = JSON.parse(value);
                account[header] = value;
                return;
              } catch (e) {
                // Keep as string if parsing fails
              }
            }

            // Detect booleans
            if (value === 'true') {
              account[header] = true;
              return;
            }
            if (value === 'false') {
              account[header] = false;
              return;
            }

            // Detect numbers (integer or float)
            if (/^-?\d+$/.test(value)) {
              account[header] = parseInt(value, 10);
              return;
            }
            if (/^-?\d+\.\d+$/.test(value)) {
              account[header] = parseFloat(value);
              return;
            }

            // Detect dates if field name contains "date" (case-insensitive)
            if (header.toLowerCase().includes('date')) {
              const parsedDate = new Date(value);
              if (!isNaN(parsedDate.getTime())) {
                account[header] = parsedDate;
                return;
              }
            }

            // Keep as string
            account[header] = value;
          });
          return account;
        });
      }

      // Import each account
      const idToken = await auth.currentUser.getIdToken(true);
      let successCount = 0;
      let failCount = 0;

      for (const account of accounts) {
        try {
          const response = await fetch(`${API_BASE_URL}/admin/importAccount`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              ...account,
              accountType: importAccountType,
            }),
          });

          const data = await response.json();
          if (data.success) {
            successCount++;
          } else {
            failCount++;
            console.error(`Failed to import ${account.email}:`, data.error);
          }
        } catch (error) {
          failCount++;
          console.error(`Error importing ${account.email}:`, error);
        }
      }

      // Refresh the accounts list
      const db = getFirestore(app);
      const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
      const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(accountsList);

      setImportMessage(`Import complete: ${successCount} successful, ${failCount} failed`);
    } catch (error) {
      setErrorMessage('Error importing accounts: ' + error.message);
    } finally {
      setIsImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleShippingToggle = async (userId, newShippedStatus) => {
    if (newShippedStatus) {
      // Show confirmation modal asking about email subscription
      setShipSubscribeUserId(userId);
      setShowShipSubscribeModal(true);
      return;
    }
    // Toggling shipped off — no confirmation needed
    try {
      const db = getFirestore(app);
      const userRef = doc(db, 'accountsInfo', userId);
      await updateDoc(userRef, {
        shipped: newShippedStatus
      });
      const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
      const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(accountsList);
    } catch (error) {
      console.error('Error updating shipped status:', error);
    }
  };

  const handleShipWithSubscription = async (subscribe) => {
    const userId = shipSubscribeUserId;
    setShowShipSubscribeModal(false);
    setShipSubscribeUserId(null);
    setShipUpdateDate(true); // Reset for next time
    if (!userId) return;
    try {
      const db = getFirestore(app);
      const userRef = doc(db, 'accountsInfo', userId);
      const updateData = { shipped: true };
      if (shipUpdateDate) {
        updateData.shippedAt = serverTimestamp();
      }
      if (subscribe) {
        updateData.receiveEmailUpdates = true;
        // Only generate a new key if the account doesn't already have one,
        // to avoid breaking previously sent unsubscribe links
        const existingUser = users.find(u => u.id === userId);
        if (!existingUser?.emailUpdatesIdKey) {
          updateData.emailUpdatesIdKey = crypto.randomUUID();
        }
      }
      await updateDoc(userRef, updateData);
      const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
      const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(accountsList);
    } catch (error) {
      console.error('Error updating shipped status:', error);
    }
  };

  const handlePreparingForShippingToggle = async (userId, newPreparingStatus) => {
    try {
      const db = getFirestore(app);
      const userRef = doc(db, 'accountsInfo', userId);
      await updateDoc(userRef, {
        preparingForShipping: newPreparingStatus
      });
      // Refresh users list
      const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
      const accountsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(accountsList);
    } catch (error) {
      console.error('Error updating preparing for shipping status:', error);
    }
  };

  const handleSignOut = async () => {
    const auth = getAuth(app);
    try {
      await signOut(auth).then(() => {
        window.location.reload();
      });
    } catch (err) {
      setErrorMessage('Failed to sign out');
    }
  };

  const toggleIglooAccount = (accountId) => {
    setExpandedIglooAccounts(prev => ({ ...prev, [accountId]: !prev[accountId] }));
  };

  const expandAllIglooAccounts = () => {
    const all = {};
    iglooAccountGroups.accounts.forEach(g => { all[g.account.id] = true; });
    if (iglooAccountGroups.orphaned.length) all.orphaned = true;
    setExpandedIglooAccounts(all);
  };

  const collapseAllIglooAccounts = () => setExpandedIglooAccounts({});

  // Standalone shareable page (mirrors /devices/:deviceId/remote) — the URL is
  // the source of truth, so opening metrics always yields a copyable link.
  const handleViewIglooMetrics = (igloo) => {
    navigate('/igloos/' + igloo.id + '/metrics');
  };

  const toggleDeviceAccount = (accountId) => {
    setExpandedDeviceAccounts(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  const expandAllDeviceAccounts = () => {
    const expanded = {};
    deviceAccounts.forEach(account => {
      expanded[account.id] = true;
    });
    setExpandedDeviceAccounts(expanded);
  };

  const collapseAllDeviceAccounts = () => {
    setExpandedDeviceAccounts({});
  };

  // O(1) device lookup map instead of repeated .find() over the full array
  const deviceMap = useMemo(() => {
    const map = new Map();
    devices.forEach(d => map.set(d.id, d));
    return map;
  }, [devices]);

  // Get devices that belong to a specific account (via families array)
  // Includes placeholder entries for families that don't have a deviceManagement doc yet
  const getDevicesForAccount = useCallback((account) => {
    const families = account.families || [];
    return families.map(familyId => {
      const realDevice = deviceMap.get(familyId);
      if (realDevice) {
        return realDevice;
      }
      // Placeholder for families without a deviceManagement doc
      return {
        id: familyId,
        familyName: allFamilyNames[familyId] || familyId,
        hasDeviceManagement: false
      };
    });
  }, [deviceMap, allFamilyNames]);

  // Get devices that don't belong to any account (orphaned devices)
  const orphanedDevices = useMemo(() => {
    const allAccountFamilies = new Set();
    deviceAccounts.forEach(account => {
      (account.families || []).forEach(familyId => {
        allAccountFamilies.add(familyId);
      });
    });
    return devices.filter(device => !allAccountFamilies.has(device.id));
  }, [deviceAccounts, devices]);

  // familyId -> account object (reverse lookup), mirroring StatusTab's familyToAccount.
  const familyToAccount = useMemo(() => {
    const map = {};
    deviceAccounts.forEach((a) => (a.families || []).forEach((fid) => { map[fid] = a; }));
    return map;
  }, [deviceAccounts]);

  // Devices on the same account as `device`, for the device-switch dropdown in the
  // live-log viewer — scopes it to the account instead of the whole fleet. Orphaned
  // devices with no account get just themselves. (The standalone remote-control page
  // rebuilds this scoping from Firestore for cold loads — see RemoteControlPage.js.)
  const scopeDevicesToAccount = useCallback((device) => {
    if (!device) return [];
    const account = familyToAccount[device.id];
    if (!account) return [device];
    const fams = new Set(account.families || []);
    return devices.filter((d) => fams.has(d.id));
  }, [familyToAccount, devices]);

  const instantLogDevices = useMemo(
    () => scopeDevicesToAccount(selectedInstantLogDevice),
    [scopeDevicesToAccount, selectedInstantLogDevice]
  );

  // Fetch last igloo times for all families (called lazily when "Recent Igloo" sort is selected)
  const fetchIglooTimes = async () => {
    if (iglooTimesLoading) return;
    setIglooTimesLoading(true);
    const db = getFirestore(app);
    const allFamilyIds = new Set();
    deviceAccounts.forEach(account => {
      (account.families || []).forEach(fid => allFamilyIds.add(fid));
    });
    const times = {};
    try {
      // Fetch live sessions once
      const liveSnap = await getDocs(collection(db, 'iglooSessionsLive'));
      for (const sessionDoc of liveSnap.docs) {
        const participants = sessionDoc.data().participants || [];
        for (const participant of participants) {
          if (!allFamilyIds.has(participant.familyId)) continue;
          const intervals = participant.intervals || [];
          const lastInterval = intervals[intervals.length - 1];
          if (lastInterval?.joinedAt && !lastInterval?.leftAt) {
            times[participant.familyId] = Date.now();
          } else if (lastInterval?.leftAt) {
            const leftTime = lastInterval.leftAt.toMillis ? lastInterval.leftAt.toMillis() : lastInterval.leftAt;
            if (!times[participant.familyId] || leftTime > times[participant.familyId]) {
              times[participant.familyId] = leftTime;
            }
          }
        }
      }
    } catch (e) { /* ignore */ }
    // Fetch history per family
    await Promise.all(
      Array.from(allFamilyIds).map(async (familyId) => {
        try {
          // Newest sessions first (composite index); falls back to the previous
          // unordered limit(50) query until the index is deployed.
          const { docs: historyDocs } = await getHistoryDocsNewestFirst(db, { familyId }, { limit: 5, legacyLimit: 50 });
          for (const sessionDoc of historyDocs) {
            const endedAt = sessionDoc.data().endedAt;
            const endTime = endedAt?.toMillis ? endedAt.toMillis() : endedAt;
            if (endTime && (!times[familyId] || endTime > times[familyId])) {
              times[familyId] = endTime;
            }
          }
        } catch (e) { /* ignore */ }
      })
    );
    setLastIglooTimes(times);
    setIglooTimesLoading(false);
  };

  // Get most recent igloo time across all families in an account
  const getAccountLatestIgloo = (account) => {
    const families = account.families || [];
    let latest = null;
    families.forEach(fid => {
      const t = lastIglooTimes[fid];
      if (t && (latest === null || t > latest)) latest = t;
    });
    return latest;
  };

  // Sort device accounts based on selected sort mode (memoized)
  const sortedDeviceAccounts = useMemo(() => [...deviceAccounts].sort((a, b) => {
    switch (deviceSortMode) {
      case 'recentSetup': {
        const aTime = getAccountSetupTime(a) || 0;
        const bTime = getAccountSetupTime(b) || 0;
        if (aTime !== bTime) return bTime - aTime;
        return (a.email || '').localeCompare(b.email || '');
      }
      case 'recentSignin': {
        const aTime = getAccountLatestSignin(a) || 0;
        const bTime = getAccountLatestSignin(b) || 0;
        if (aTime !== bTime) return bTime - aTime;
        return (a.email || '').localeCompare(b.email || '');
      }
      case 'recentIgloo': {
        const aTime = getAccountLatestIgloo(a) || 0;
        const bTime = getAccountLatestIgloo(b) || 0;
        if (aTime !== bTime) return bTime - aTime;
        return (a.email || '').localeCompare(b.email || '');
      }
      default: // alphabetic
        return (a.email || '').localeCompare(b.email || '');
    }
  }), [deviceAccounts, deviceSortMode, firstSigninTimes, lastIglooTimes]);

  const fetchAndDisplayLog = async (body, label = 'log') => {
    setLogLoading(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/admin/fetchLog`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch ${label}: ${response.status} ${response.statusText}`);
      }
      setLogContent(await response.text());
    } catch (error) {
      console.error(`Error fetching ${label}:`, error);
      setLogContent(`Error fetching ${label}: ${error.message}`);
    } finally {
      setLogLoading(false);
    }
  };

  const handleViewLog = async (familyID, familyName, logPath) => {
    setLogContent('Loading log...');
    setCurrentLogName(familyName);
    setCurrentLogFamilyID(familyID);
    setCurrentLogPath(logPath);
    setIsCrashLog(false);
    setLogModalVisible(true);
    // Use logPath if available (new format), fall back to legacy format
    const body = logPath ? { logPath } : { logName: `${familyID}.log` };
    await fetchAndDisplayLog(body);
  };

  const handleViewCrashLog = async (familyID, familyName, crashLogPath) => {
    if (!crashLogPath) return;
    setLogContent('Loading crash log...');
    setCurrentLogName(`${familyName} (crash)`);
    setCurrentLogFamilyID(familyID);
    setCurrentLogPath(crashLogPath);
    setIsCrashLog(true);
    setLogModalVisible(true);
    await fetchAndDisplayLog({ logPath: crashLogPath }, 'crash log');
  };

  const handleRequestLogDump = async (familyID) => {
    try {
      const db = getFirestore();
      const dataRequestsRef = doc(db, 'deviceManagement', familyID, 'remoteManagement', 'dataRequests');
      await setDoc(dataRequestsRef, { logDump: true }, { merge: true });
    } catch (error) {
      console.error('Error requesting log dump:', error);
    }
  };

  // Normal log reload: snapshot the current logDumpTimestamp, ask the device to dump a fresh logcat,
  // then let the watcher useEffect fetch it once the timestamp advances.
  const handleLogViewerReload = async () => {
    if (!currentLogFamilyID) return;
    const device = devices.find(d => d.id === currentLogFamilyID);
    if (!device) return;
    const ts = device.logDumpTimestamp;
    logTimestampBeforeReload.current = ts?.toMillis?.() ?? ts?.seconds * 1000 ?? 0;
    setLogReloading(true);
    await handleRequestLogDump(currentLogFamilyID);
  };

  // Crash log reload: crash logs are only produced on app-startup-after-crash (see Logging.kt
  // uploadCrashLog), so we just re-fetch whatever crashLogPath the device's adminVisible doc
  // currently advertises — that path is updated live via the devices listener.
  const handleCrashLogViewerReload = async () => {
    if (!currentLogFamilyID) return;
    const device = devices.find(d => d.id === currentLogFamilyID);
    if (!device?.crashLogPath) return;
    setCurrentLogPath(device.crashLogPath);
    await fetchAndDisplayLog({ logPath: device.crashLogPath }, 'crash log');
  };

  // Watch for the device's logDumpTimestamp to change after a reload request
  useEffect(() => {
    if (!logReloading || !currentLogFamilyID || !logModalVisible) return;
    const device = devices.find(d => d.id === currentLogFamilyID);
    if (!device) return;

    // Error — stop waiting and tell the user
    if (device.logDumpStatus === 'error') {
      setLogReloading(false);
      return;
    }

    // Still uploading — keep waiting
    if (device.logDumpStatus === 'uploading') return;

    // Check if the timestamp is newer than what we had before
    const ts = device.logDumpTimestamp;
    const currentMs = ts?.toMillis?.() ?? ts?.seconds * 1000 ?? 0;
    if (currentMs <= logTimestampBeforeReload.current) return; // not yet updated

    // New log is ready — fetch it
    setLogReloading(false);
    setCurrentLogPath(device.logDumpPath);
    logTimestampBeforeReload.current = null;
    fetchAndDisplayLog({ logPath: device.logDumpPath });
  }, [logReloading, currentLogFamilyID, logModalVisible, devices]);

  const handleViewInstantLog = (device) => {
    setSelectedInstantLogDevice(device);
    setShowInstantLogViewer(true);
  };

  const handleBackFromInstantLog = () => {
    setShowInstantLogViewer(false);
    setSelectedInstantLogDevice(null);
  };

  // The global settings button is tab-aware: the two stacks are disjoint (a knob
  // belongs to exactly one scope), so "Global Settings" means Hearth settings on
  // the devices tab and Higlo settings on the igloos tab.
  const globalSettingsScope = activeTab === 'igloos' ? IGLOO_SCOPE : HEARTH_SCOPE;

  const handleViewSettings = () => {
    setSettingsViewerScope(globalSettingsScope);
  };

  const handleBackFromSettings = () => {
    setSettingsViewerScope(null);
  };

  const handleViewHotplug = (device) => {
    setSelectedHotplugDevice(device);
    setShowHotplugViewer(true);
  };

  const handleBackFromHotplug = () => {
    setShowHotplugViewer(false);
    setSelectedHotplugDevice(null);
  };

  const handleViewAlerts = (device) => {
    setAlertsViewerCtx({
      familyId: device.id,
      familyName: device.familyName || device.id,
    });
  };

  const handleViewAlertsInbox = () => {
    setAlertsViewerCtx({ familyId: null, familyName: null });
  };

  const handleCloseAlerts = () => {
    setAlertsViewerCtx(null);
  };

  const handleViewOverrideSettings = (device) => {
    setOverrideViewerCtx({
      scope: HEARTH_SCOPE,
      entity: { id: device.id, name: device.familyName || device.id },
    });
  };

  // Per-igloo override editor — the generic twin of the per-device one above.
  const handleViewIglooSettings = (igloo) => {
    setOverrideViewerCtx({
      scope: IGLOO_SCOPE,
      entity: { id: igloo.id, name: igloo.iglooName || igloo.id },
    });
  };

  const handleBackFromOverrideSettings = () => {
    setOverrideViewerCtx(null);
  };

  const handleViewTvInfo = (device) => {
    setSelectedTvInfoDevice(device);
    setShowTvInfoViewer(true);
  };

  const handleBackFromTvInfo = () => {
    setShowTvInfoViewer(false);
    setSelectedTvInfoDevice(null);
  };

  // Show InstantLogViewer if selected
  if (showInstantLogViewer && selectedInstantLogDevice) {
    return (
      <InstantLogViewer 
        initialDevice={selectedInstantLogDevice}
        onBack={handleBackFromInstantLog}
        allDevices={instantLogDevices}
      />
    );
  }

  // Show SettingsViewer if selected
  if (settingsViewerScope) {
    return (
      <SettingsViewer
        scope={settingsViewerScope}
        onBack={handleBackFromSettings}
      />
    );
  }

  // Show HotplugSettingsViewer if selected
  if (showHotplugViewer && selectedHotplugDevice) {
    return (
      <HotplugSettingsViewer
        device={selectedHotplugDevice}
        onBack={handleBackFromHotplug}
      />
    );
  }

  // Show OverrideSettingsViewer if selected (Hearth or Higlo scope)
  if (overrideViewerCtx) {
    return (
      <OverrideSettingsViewer
        scope={overrideViewerCtx.scope}
        entity={overrideViewerCtx.entity}
        onBack={handleBackFromOverrideSettings}
      />
    );
  }

  // Show TvInfoViewer if selected
  if (showTvInfoViewer && selectedTvInfoDevice) {
    return (
      <TvInfoViewer
        device={selectedTvInfoDevice}
        onBack={handleBackFromTvInfo}
      />
    );
  }

  return (
    <div className="mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-3">
          <Settings className="w-8 h-8" />
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowMessageInbox(true)}
            className="flex items-center gap-2 relative"
          >
            <MessageCircle className="w-4 h-4" />
            Messages
            {conversations.reduce((sum, c) => sum + (c.unreadByAdmin || 0), 0) > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center animate-pulse">
                {(() => {
                  const total = conversations.reduce((sum, c) => sum + (c.unreadByAdmin || 0), 0);
                  return total > 99 ? '99+' : total;
                })()}
              </span>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={handleViewAlertsInbox}
            className="flex items-center gap-2 relative"
          >
            <AlertTriangle className="w-4 h-4" />
            Alerts
            {Object.values(alertUnreadByFamily).reduce((s, n) => s + n, 0) > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-xs font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center animate-pulse">
                {(() => {
                  const total = Object.values(alertUnreadByFamily).reduce((s, n) => s + n, 0);
                  return total > 99 ? '99+' : total;
                })()}
              </span>
            )}
          </Button>
          <Button variant="outline" onClick={handleViewSettings} className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            {globalSettingsScope.globalTitle}
          </Button>
          <Button variant="destructive" onClick={handleSignOut} className="flex items-center gap-2">
            <LogOut className="w-4 h-4" />
            Sign Out
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <Tabs
        value={activeTab}
        onValueChange={(tab) => {
          // Radix emits onValueChange for both the focus and the click of one
          // tap, before React re-renders — guard against the live URL so a
          // tab switch pushes exactly one history entry.
          if (window.location.pathname !== '/' + tab) {
            navigate('/' + tab);
          }
        }}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="accounts" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Accounts
          </TabsTrigger>
          <TabsTrigger value="devices" className="flex items-center gap-2">
            <Monitor className="w-4 h-4" />
            Devices
          </TabsTrigger>
          <TabsTrigger value="igloos" className="flex items-center gap-2">
            <Snowflake className="w-4 h-4" />
            Igloos
          </TabsTrigger>
          <TabsTrigger value="health" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Health
          </TabsTrigger>
          <TabsTrigger value="sessions" className="flex items-center gap-2">
            <Play className="w-4 h-4" />
            Sessions
          </TabsTrigger>
          <TabsTrigger value="status" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Status
          </TabsTrigger>
        </TabsList>

        {/* Accounts Tab */}
        <TabsContent value="accounts" className="space-y-6">
          {/* Accounts List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Accounts List
                  {billingAttentionDegraded && (
                    <span
                      className="text-xs font-normal text-amber-600"
                      title="The billing-attention sweep failed or was partial — payment-problem dots on the Billing buttons may be missing. Cancel-request dots are unaffected."
                    >
                      billing status unavailable
                    </span>
                  )}
                </CardTitle>
                <div className="flex items-center gap-3 flex-wrap justify-end">
                  {/* Tier filter chips — empty selection = show all */}
                  <div className="flex items-center gap-1">
                    {TIER_NUMBERS.map(t => {
                      const on = !!tierFilter[t];
                      return (
                        <button
                          key={t}
                          onClick={() => setTierFilter(prev => ({ ...prev, [t]: !prev[t] }))}
                          title={`${TIERS[t].name} — ${TIERS[t].description}`}
                          className={`px-2 py-0.5 rounded-full border text-xs font-medium transition-colors ${on ? TIERS[t].chipOnClass : TIERS[t].chipOffClass}`}
                        >
                          {TIERS[t].label} ({tierCounts[t]})
                        </button>
                      );
                    })}
                  </div>
                  {/* Account-type tick menu — applies to every view */}
                  <AccountTypeFilter
                    counts={typeCounts}
                    hidden={typeFilter}
                    onToggle={(key) => setTypeFilter(prev => {
                      const next = { ...prev };
                      if (next[key]) delete next[key]; else next[key] = true;
                      return next;
                    })}
                    onShowAll={() => setTypeFilter({})}
                    onHideAll={() => setTypeFilter(
                      Object.fromEntries(allTypeKeys.map(k => [k, true]))
                    )}
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">View:</span>
                    <Select value={viewMode} onValueChange={handleViewModeChange}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Default</SelectItem>
                        <SelectItem value="usage">Usage</SelectItem>
                        <SelectItem value="billing">Billing</SelectItem>
                        <SelectItem value="history">History</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Subtext is absolutely positioned so it hangs under the
                      button without pushing it out of line with the other
                      header controls. */}
                  <div className="relative">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUpdateAnalysis}
                      disabled={analyticsRunning}
                      title="Re-scan all Higlo sessions and recompute per-family multi-person call analytics"
                      className="flex items-center gap-2"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${analyticsRunning ? 'animate-spin' : ''}`} />
                      {analyticsRunning ? 'Analyzing…' : 'Update analysis'}
                    </Button>
                    <span className={`absolute top-full right-0 mt-0.5 whitespace-nowrap text-[10px] ${(analyticsError || analyticsFetchDegraded) ? 'text-red-500' : 'text-muted-foreground'}`}>
                      {analyticsError
                        ? 'last run failed'
                        : analyticsFetchDegraded
                          ? 'analytics unavailable'
                          : analyticsMeta?.lastRunAt
                            ? `updated ${formatRelativeTime(usageToMs(analyticsMeta.lastRunAt))}`
                            : 'no analysis yet'}
                    </span>
                  </div>
                </div>
              </div>
              {/* History view: the shared time axis + how far back the daily
                  device history has been backfilled. The backfill walks
                  further back on each run, so the button doubles as "fetch
                  more history" and needs to say so. */}
              {viewMode === 'history' && (
                <div className="mt-3 flex flex-col gap-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-medium">Range:</span>
                    <div className="flex items-center gap-1">
                      {HISTORY_RANGES.map(r => {
                        const on = (historyRange?.preset || DEFAULT_HISTORY_RANGE.preset) === r.key;
                        return (
                          <button
                            key={r.key}
                            onClick={() => setHistoryRange({ preset: r.key })}
                            className={`px-2 py-0.5 rounded-full border text-xs font-medium transition-colors ${on ? 'bg-slate-700 text-white border-slate-700' : 'text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                          >
                            {r.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-muted-foreground">from</span>
                      <input
                        type="month"
                        className={`border rounded px-1 py-0.5 text-xs ${historyRangeInvalid ? 'border-amber-500 text-amber-700' : ''}`}
                        value={historyRange?.fromMonth || toMonthStr(historyWindow.startMs)}
                        max={toMonthStr(Date.now())}
                        onChange={(e) => setHistoryRange(prev => ({
                          preset: 'custom',
                          fromMonth: e.target.value,
                          toMonth: prev?.toMonth || toMonthStr(Date.now()),
                        }))}
                      />
                      <span className="text-muted-foreground">to</span>
                      <input
                        type="month"
                        className={`border rounded px-1 py-0.5 text-xs ${historyRangeInvalid ? 'border-amber-500 text-amber-700' : ''}`}
                        value={historyRange?.toMonth || toMonthStr(historyWindow.endMs)}
                        max={toMonthStr(Date.now())}
                        onChange={(e) => setHistoryRange(prev => ({
                          preset: 'custom',
                          fromMonth: prev?.fromMonth || toMonthStr(historyWindow.startMs),
                          toMonth: e.target.value,
                        }))}
                      />
                    </div>
                    {historyRangeInvalid && (
                      <span className="text-[10px] text-amber-700">
                        that month range is inverted or incomplete — the chart is
                        showing {new Date(historyWindow.startMs).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                        {' to now'} instead
                      </span>
                    )}
                    {analyticsMeta && (() => {
                      // Denominator is every family the sweep touched, NOT
                      // complete+incomplete: a family whose rollup throws is in
                      // neither bucket, and would otherwise vanish from the
                      // count and let a permanent failure read as "all done".
                      const complete = analyticsMeta.dailyFamiliesComplete ?? 0;
                      const swept = analyticsMeta.dailyFamiliesSwept
                        ?? (complete + (analyticsMeta.dailyFamiliesIncomplete ?? 0));
                      const errors = analyticsMeta.dailyFamilyErrors ?? 0;
                      const done = swept > 0 && complete >= swept;
                      // The date EVERY family reaches — the only one that is a
                      // claim about the fleet. The deepest single family would
                      // otherwise speak for all of them.
                      const back = analyticsMeta.dailyOldestDayEveryFamily;
                      return (
                        <span className={`text-[10px] ${errors > 0 ? 'text-amber-700' : 'text-muted-foreground'}`}>
                          {!back
                            ? 'no device history computed yet — press Update analysis'
                            : done
                              ? `device history complete for every family back to ${back}`
                              : `device history backfilling — ${complete} of ${swept} families complete; every family covered back to ${back}; press Update analysis again for more`}
                          {errors > 0 && ` · ${errors} family(ies) failed on the last run — see the function logs`}
                          {analyticsMeta.backfillStoppedForTime && ' · last run hit its time budget'}
                        </span>
                      );
                    })()}
                  </div>
                  {dailyAnalyticsError && (
                    <div className="text-xs text-amber-700">{dailyAnalyticsError}</div>
                  )}
                  {messageActivityError && (
                    <div className="text-xs text-amber-700">
                      {messageActivityUnavailable
                        ? 'Message activity could not be loaded (permissions or connectivity) — the message lane is hidden. Milestones, device state and calls are unaffected.'
                        : `Message activity could not be loaded for ${messageActivityFailedIds.size} account(s). Their message lane is marked with a dashed amber line — unknown, not “no messages”.`}
                    </div>
                  )}
                  <HistoryLegend />
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                {accountCommentsError && commentsViewActive && (
                  <div className="text-xs text-amber-700 mb-2">
                    {accountCommentsUnavailable
                      ? 'Comments could not be loaded (permissions or connectivity) — the column is read-only until access is restored. Comments already saved are unaffected.'
                      : 'Some comments could not be loaded (permissions or connectivity). Those cells show the last value read, or “unavailable”, and cannot be edited until the read succeeds. Comments already saved are unaffected.'}
                  </div>
                )}
                {viewMode === 'billing' ? (
                  <BillingAccountsTable
                    users={displayedUsers}
                    records={accountBilling}
                    accountUsage={accountUsage}
                    signedInCounts={signedInCounts}
                    billingAttention={billingAttention}
                    comments={accountComments}
                    commentsUnavailable={accountCommentsUnavailable}
                    commentsLoading={accountCommentsLoading}
                    commentFailedIds={accountCommentFailedIds}
                    onCommentSaved={refreshAccountComment}
                    SortHeader={SortHeader}
                    onOpenDoc={setDocViewerTarget}
                    onOpenChat={handleOpenChat}
                    onOpenBilling={setBillingCheckTarget}
                    getUnreadCountForAccount={getUnreadCountForAccount}
                    getConversationStatus={getConversationStatus}
                    hasDraftForAccount={hasDraftForAccount}
                  />
                ) : (
                <table className="w-full">
                  <thead>
                    {viewMode === 'usage' ? (
                      <tr className="border-b">
                        <SortHeader field="email">Email</SortHeader>
                        <SortHeader field="name">Name</SortHeader>
                        <SortHeader field="families" align="center">Families</SortHeader>
                        <SortHeader field="tier" align="center">Tier</SortHeader>
                        <SortHeader field="lastCall" align="center">Last Call</SortHeader>
                        <SortHeader field="calls3mo" align="center">Calls (3mo)</SortHeader>
                        <th className="text-left py-1.5 px-2 font-medium text-sm">Timeline (3mo)</th>
                        <th className="text-center py-1.5 px-2 font-medium text-sm">Messages</th>
                      </tr>
                    ) : viewMode === 'history' ? (
                      <tr className="border-b">
                        <SortHeader field="email">Email</SortHeader>
                        <SortHeader field="name">Name</SortHeader>
                        <SortHeader field="families" align="center">Families</SortHeader>
                        <SortHeader field="tier" align="center">Tier</SortHeader>
                        <SortHeader field="ordered" align="center">Ordered</SortHeader>
                        <SortHeader field="delivered" align="center">Delivered</SortHeader>
                        <SortHeader field="lastCall" align="center">Last Call</SortHeader>
                        {/* The month scale is drawn once, here, so the table
                            reads as one chart rather than N small ones. */}
                        <th className="text-left py-1.5 px-2 font-medium text-sm align-bottom">
                          <div>History</div>
                          <HistoryAxisHeader
                            windowStartMs={historyWindow.startMs}
                            windowEndMs={historyWindow.endMs}
                            width={HISTORY_TIMELINE_WIDTH}
                          />
                        </th>
                        {/* Fixed width, matching AccountCommentCell's own box,
                            so the column is the same size whether or not a cell
                            is being edited. */}
                        <th className="text-left py-1.5 px-2 font-medium text-sm w-[236px]">Comment</th>
                        <th className="text-center py-1.5 px-2 font-medium text-sm">Billing</th>
                        <th className="text-center py-1.5 px-2 font-medium text-sm">Messages</th>
                      </tr>
                    ) : (
                    <tr className="border-b">
                      <SortHeader field="email">Email</SortHeader>
                      <SortHeader field="name">Name</SortHeader>
                      <SortHeader field="batch">Batch</SortHeader>
                      <SortHeader field="country">Country</SortHeader>
                      <SortHeader field="type" align="center">Type</SortHeader>
                      <SortHeader field="families" align="center">Families</SortHeader>
                      <SortHeader field="status" align="center">Status</SortHeader>
                      <th className="text-center py-1.5 px-2 font-medium text-sm">Preparing</th>
                      <th className="text-center py-1.5 px-2 font-medium text-sm">Shipped</th>
                      <th className="text-center py-1.5 px-2 font-medium text-sm">Delivered</th>
                      <th className="text-center py-1.5 px-2 font-medium text-sm">Messages</th>
                      <th className="text-right py-1.5 px-2 font-medium text-sm">Actions</th>
                    </tr>
                    )}
                  </thead>
                  <tbody>
                    {viewMode === 'usage' ? displayedUsers.map(user => {
                      const usage = accountUsage[user.id] || { tier: 5, calls: [], lastCall: null, lastCallMs: null, calls3mo: 0, familyStatuses: [] };
                      const tierInfo = TIERS[usage.tier];
                      return (
                        <tr key={user.id} className="border-b hover:bg-muted/50 transition-colors">
                          <td className="py-1.5 px-2">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              <div>
                                <span className="font-medium truncate block text-blue-600 hover:underline cursor-pointer" onClick={() => setDocViewerTarget({ type: 'account', id: user.id })}>{user.email}</span>
                                <span className="text-xs text-muted-foreground block hover:text-blue-600 hover:underline cursor-pointer" onClick={() => setDocViewerTarget({ type: 'account', id: user.id })}>{user.id}</span>
                              </div>
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-sm">
                            {user.nameAtOrder || '-'}
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                              <Home className="w-3 h-3" />
                              <span>{signedInCounts[user.id] ?? 0}/{user.numOfFamilies ?? 0}</span>
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <Badge className={`text-xs ${tierInfo.badgeClass}`} title={`${tierInfo.name} — ${tierInfo.description}`}>
                              {tierInfo.label}
                            </Badge>
                            <div className="text-[10px] text-muted-foreground mt-0.5">{tierInfo.name}</div>
                          </td>
                          <td className="py-1.5 px-2 text-center text-sm">
                            {usage.lastCall ? (
                              <>
                                <div>{formatRelativeTime(usage.lastCall.startMs)}</div>
                                <div className="text-[10px] text-muted-foreground">{formatCallDuration(usage.lastCall.durationSeconds)}</div>
                              </>
                            ) : usage.lastCallMs ? (
                              // Last call predates the retained-calls window —
                              // date known from the all-time summary, duration not.
                              <div>{formatRelativeTime(usage.lastCallMs)}</div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="py-1.5 px-2 text-center text-sm">
                            {usage.calls3mo}
                          </td>
                          <td className="py-1.5 px-2">
                            <UsageTimeline calls={usage.calls} familyStatuses={usage.familyStatuses} />
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenChat(user)}
                                className="relative flex items-center gap-1"
                              >
                                <MessageCircle className="w-4 h-4" />
                                {getUnreadCountForAccount(user.id) > 0 && (
                                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
                                    {getUnreadCountForAccount(user.id) > 9 ? '9+' : getUnreadCountForAccount(user.id)}
                                  </span>
                                )}
                                {getUnreadCountForAccount(user.id) === 0 &&
                                 getConversationStatus(user.id) === 'open' && (
                                  <span className="absolute -top-0.5 -right-0.5 bg-green-500 rounded-full h-2.5 w-2.5"></span>
                                )}
                                {/* Saved draft — own corner, so it never hides the unread/open state */}
                                {hasDraftForAccount(user.id) && (
                                  <span
                                    title="You have a saved draft for this account"
                                    className="absolute -bottom-0.5 -left-0.5 bg-blue-500 rounded-full h-2.5 w-2.5"
                                  ></span>
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    }) : viewMode === 'history' ? displayedUsers.map(user => {
                      const usage = accountUsage[user.id] || { tier: 5, lastCall: null, lastCallMs: null };
                      const history = accountHistory[user.id] || { milestones: [], dailyStates: [], calls: [], orderedMs: null };
                      const tierInfo = TIERS[usage.tier];
                      // A failed read must not be drawn as "no messages" —
                      // pass nothing and let the lane stay empty.
                      const activity = messageActivityFailedIds.has(user.id)
                        ? null
                        : messageActivity[user.id] || null;
                      // Same red-dot rule as the Billing view, so the two
                      // never disagree about which accounts need attention.
                      const billingAttentionReasons = [...new Set([
                        ...(billingAttention[user.id] || []),
                        ...(user.subscriptionCancelRequested ? ['cancel_requested'] : []),
                      ])];
                      return (
                        <tr key={user.id} className="border-b hover:bg-muted/50 transition-colors">
                          <td className="py-1.5 px-2">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              <div>
                                <span className="font-medium truncate block text-blue-600 hover:underline cursor-pointer" onClick={() => setDocViewerTarget({ type: 'account', id: user.id })}>{user.email}</span>
                                <span className="text-xs text-muted-foreground block hover:text-blue-600 hover:underline cursor-pointer" onClick={() => setDocViewerTarget({ type: 'account', id: user.id })}>{user.id}</span>
                              </div>
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-sm">
                            {user.nameAtOrder || '-'}
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                              <Home className="w-3 h-3" />
                              <span>{signedInCounts[user.id] ?? 0}/{user.numOfFamilies ?? 0}</span>
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <Badge className={`text-xs ${tierInfo.badgeClass}`} title={`${tierInfo.name} — ${tierInfo.description}`}>
                              {tierInfo.label}
                            </Badge>
                          </td>
                          <td className="py-1.5 px-2 text-center text-sm">
                            {history.orderedMs ? (
                              <div title={new Date(history.orderedMs).toISOString()}>
                                <div>{formatShortDate(history.orderedMs)}</div>
                                <div className="text-[10px] text-muted-foreground">
                                  {formatElapsed(history.orderedMs, historyNowMs)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="py-1.5 px-2 text-center text-sm">
                            {history.delivered ? (
                              <div title={history.delivered.detail
                                ? `${history.delivered.detail} — first on ${new Date(history.delivered.ms).toISOString()}`
                                : new Date(history.delivered.ms).toISOString()}>
                                <div>{formatShortDate(history.delivered.ms)}</div>
                                <div className="text-[10px] text-muted-foreground">
                                  {formatElapsed(history.delivered.ms, historyNowMs)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="py-1.5 px-2 text-center text-sm">
                            {usage.lastCall ? (
                              <>
                                <div>{formatElapsed(usage.lastCall.startMs, historyNowMs)}</div>
                                <div className="text-[10px] text-muted-foreground">{formatCallDuration(usage.lastCall.durationSeconds)}</div>
                              </>
                            ) : usage.lastCallMs ? (
                              <div>{formatElapsed(usage.lastCallMs, historyNowMs)}</div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="py-1.5 px-2">
                            <HistoryTimeline
                              milestones={history.milestones}
                              dailyStates={history.dailyStates}
                              calls={history.calls}
                              messages={activity}
                              messagesUnavailable={messageActivityUnavailable}
                              messagesFailed={messageActivityFailedIds.has(user.id)}
                              dailyLoading={dailyAnalyticsLoading}
                              dailyError={!!dailyAnalyticsError}
                              messagesLoading={messageActivityLoading}
                              windowStartMs={historyWindow.startMs}
                              windowEndMs={historyWindow.endMs}
                              width={HISTORY_TIMELINE_WIDTH}
                            />
                          </td>
                          <td className="py-1.5 px-2">
                            <AccountCommentCell
                              accountUUID={user.id}
                              comment={accountComments[user.id]}
                              onSaved={refreshAccountComment}
                              onRetry={refreshAccountComment}
                              disabled={accountCommentsUnavailable}
                              loading={accountCommentsLoading}
                              failed={!!accountCommentFailedIds?.has(user.id)}
                            />
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <div className="flex justify-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setBillingCheckTarget({ id: user.id, email: user.email })}
                                title={billingAttentionReasons.length
                                  ? `Needs attention: ${billingAttentionReasons.join(', ')}`
                                  : 'Review billing & manage subscription'}
                                className="relative flex items-center gap-2"
                              >
                                <CreditCard className="w-3 h-3" />
                                Billing
                                {billingAttentionReasons.length > 0 && (
                                  <span className="absolute -top-1 -right-1 bg-red-500 rounded-full h-2.5 w-2.5"></span>
                                )}
                              </Button>
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenChat(user)}
                                className="relative flex items-center gap-1"
                              >
                                <MessageCircle className="w-4 h-4" />
                                {getUnreadCountForAccount(user.id) > 0 && (
                                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
                                    {getUnreadCountForAccount(user.id) > 9 ? '9+' : getUnreadCountForAccount(user.id)}
                                  </span>
                                )}
                                {getUnreadCountForAccount(user.id) === 0 &&
                                 getConversationStatus(user.id) === 'open' && (
                                  <span className="absolute -top-0.5 -right-0.5 bg-green-500 rounded-full h-2.5 w-2.5"></span>
                                )}
                                {/* Saved draft — own corner, so it never hides the unread/open state */}
                                {hasDraftForAccount(user.id) && (
                                  <span
                                    title="You have a saved draft for this account"
                                    className="absolute -bottom-0.5 -left-0.5 bg-blue-500 rounded-full h-2.5 w-2.5"
                                  ></span>
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    }) : displayedUsers.map(user => (
                      <tr key={user.id} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="py-1.5 px-2">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <div>
                              <span className="font-medium truncate block text-blue-600 hover:underline cursor-pointer" onClick={() => setDocViewerTarget({ type: 'account', id: user.id })}>{user.email}</span>
                              <span className="text-xs text-muted-foreground block hover:text-blue-600 hover:underline cursor-pointer" onClick={() => setDocViewerTarget({ type: 'account', id: user.id })}>{user.id}</span>
                            </div>
                          </div>
                        </td>
                        <td className="py-1.5 px-2 text-sm">
                          {user.nameAtOrder || '-'}
                        </td>
                        <td className="py-1.5 px-2 text-sm">
                          {user.batch || '-'}
                        </td>
                        <td className="py-1.5 px-2 text-sm">
                          {user.shippingCountryAtOrder || '-'}
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <Badge variant={user.accountType === 'invitedByCustomerAccount' ? 'secondary' : 'default'} className="text-xs">
                            {user.accountType === 'freeHearthOwningAccount' ? 'Free Hearth' :
                             user.accountType === 'payingHearthOwningAccount' ? 'Paying Hearth' :
                             user.accountType === 'invitedByCustomerAccount' ? 'Invited' : user.accountType}
                          </Badge>
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                            <Home className="w-3 h-3" />
                            <span>{signedInCounts[user.id] ?? 0}/{user.numOfFamilies ?? 0}</span>
                          </div>
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <Badge variant={user.accountStatus === 'accountCreated' ? 'outline' : 'default'} className="text-xs">
                            {user.accountStatus}
                          </Badge>
                          {user.accountStatus === 'setupCompleted' && getAccountSetupTime(user) && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              {formatRelativeTime(getAccountSetupTime(user))}
                            </div>
                          )}
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <div className="flex justify-center">
                            <Switch
                              checked={user.preparingForShipping || false}
                              onCheckedChange={(toggledValue) => handlePreparingForShippingToggle(user.id, toggledValue)}
                            />
                          </div>
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <Switch
                              checked={user.shipped || false}
                              onCheckedChange={(toggledValue) => handleShippingToggle(user.id, toggledValue)}
                            />
                            {user.shipped && (
                              <Mail className={`w-3.5 h-3.5 ${user.receiveEmailUpdates ? 'text-blue-500' : 'text-muted-foreground/30'}`}
                                title={user.receiveEmailUpdates ? 'Subscribed to updates' : 'Not subscribed to updates'}
                              />
                            )}
                          </div>
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          {user.shipped && user.trackingNumbers?.length > 0 && (() => {
                            const deliveryDates = user.deliveryDates || [];
                            const totalTracking = user.trackingNumbers.length;
                            const deliveredCount = user.trackingNumbers.filter((_, i) => deliveryDates[i] != null).length;
                            const isFullyDelivered = deliveredCount === totalTracking;
                            const isPartiallyDelivered = deliveredCount > 0 && deliveredCount < totalTracking;
                            const undeliveredTracking = user.trackingNumbers.filter((_, i) => deliveryDates[i] == null);

                            // Find the most recent delivery date for "days ago" display
                            const mostRecentDelivery = deliveryDates
                              .filter(d => d != null)
                              .map(d => d?.toDate ? d.toDate() : (d?.seconds ? new Date(d.seconds * 1000) : null))
                              .filter(Boolean)
                              .sort((a, b) => b - a)[0];

                            return (
                              <div className="flex flex-col items-center">
                                {isFullyDelivered ? (
                                  <>
                                    <div className="flex items-center gap-1 text-green-600">
                                      <PackageCheck className="w-3.5 h-3.5" />
                                      <span className="text-xs font-medium">Delivered</span>
                                    </div>
                                    {mostRecentDelivery && (
                                      <div className="text-[10px] text-muted-foreground mt-0.5">
                                        {formatRelativeTime(mostRecentDelivery.getTime())}
                                      </div>
                                    )}
                                  </>
                                ) : isPartiallyDelivered ? (
                                  <>
                                    <div className="flex items-center gap-1 text-amber-600">
                                      <Package className="w-3.5 h-3.5" />
                                      <span className="text-xs font-medium">Partial ({deliveredCount}/{totalTracking})</span>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                      {undeliveredTracking.map((tn, i) => (
                                        <span key={tn}>
                                          {i > 0 && ', '}
                                          <a href={`https://www.royalmail.com/track-your-item#/tracking-results/${tn}`}
                                             target="_blank" rel="noopener noreferrer"
                                             className="text-blue-500 hover:underline">
                                            {tn}
                                          </a>
                                        </span>
                                      ))}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-1 text-muted-foreground">
                                      <Package className="w-3.5 h-3.5" />
                                      <span className="text-xs">Not Delivered</span>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                      {user.trackingNumbers.map((tn, i) => (
                                        <span key={tn}>
                                          {i > 0 && ', '}
                                          <a href={`https://www.royalmail.com/track-your-item#/tracking-results/${tn}`}
                                             target="_blank" rel="noopener noreferrer"
                                             className="text-blue-500 hover:underline">
                                            {tn}
                                          </a>
                                        </span>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          <div className="flex justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenChat(user)}
                              className="relative flex items-center gap-1"
                            >
                              <MessageCircle className="w-4 h-4" />
                              {getUnreadCountForAccount(user.id) > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
                                  {getUnreadCountForAccount(user.id) > 9 ? '9+' : getUnreadCountForAccount(user.id)}
                                </span>
                              )}
                              {getUnreadCountForAccount(user.id) === 0 &&
                               getConversationStatus(user.id) === 'open' && (
                                <span className="absolute -top-0.5 -right-0.5 bg-green-500 rounded-full h-2.5 w-2.5"></span>
                              )}
                              {/* Saved draft — own corner, so it never hides the unread/open state */}
                              {hasDraftForAccount(user.id) && (
                                <span
                                  title="You have a saved draft for this account"
                                  className="absolute -bottom-0.5 -left-0.5 bg-blue-500 rounded-full h-2.5 w-2.5"
                                ></span>
                              )}
                            </Button>
                          </div>
                        </td>
                        <td className="py-1.5 px-2">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setBillingCheckTarget({ id: user.id, email: user.email })}
                              title={(billingAttention[user.id]?.length || user.subscriptionCancelRequested)
                                ? `Needs attention: ${[...new Set([
                                    ...(billingAttention[user.id] || []),
                                    ...(user.subscriptionCancelRequested ? ['cancel_requested'] : []),
                                  ])].join(', ')}`
                                : 'Review billing & manage subscription'}
                              className="relative flex items-center gap-2"
                            >
                              <CreditCard className="w-3 h-3" />
                              Billing
                              {/* Server sweep OR the row's own live cancel flag */}
                              {(billingAttention[user.id]?.length > 0 || user.subscriptionCancelRequested) && (
                                <span className="absolute -top-1 -right-1 bg-red-500 rounded-full h-2.5 w-2.5"></span>
                              )}
                            </Button>
                            {(() => {
                              const isHearthOwner = user.accountType === 'freeHearthOwningAccount' || user.accountType === 'payingHearthOwningAccount';
                              const canReset = isHearthOwner && user.accountStatus === 'setupCompleted';
                              const resetTitle = !isHearthOwner
                                ? 'Reset is only available for HearthOwner accounts'
                                : user.accountStatus !== 'setupCompleted'
                                  ? 'Reset is only available for accounts that have completed setup'
                                  : 'Roll this account back to the not-yet-set-up state';
                              return (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => confirmResetUser(user)}
                                  disabled={!canReset}
                                  title={resetTitle}
                                  className="flex items-center gap-2 border-amber-500 text-amber-700 hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <RotateCcw className="w-3 h-3" />
                                  Reset
                                </Button>
                              );
                            })()}
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => confirmRemoveUser(user)}
                              className="flex items-center gap-2"
                            >
                              <Trash2 className="w-3 h-3" />
                              Remove
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                )}

                {users.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No accounts found.</p>
                  </div>
                )}

                {/* Everything filtered out — say so, and offer the way back,
                    rather than showing an empty table that looks broken. */}
                {users.length > 0 && displayedUsers.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No accounts match the current tier / type filters.</p>
                    <button
                      onClick={() => { setTierFilter({}); setTypeFilter({}); }}
                      className="text-sm text-blue-600 hover:underline mt-1"
                    >
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Add Account Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Add New Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-w-2xl">
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-2 block">Email Address</label>
                    <Input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      placeholder="Enter user email"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-2 block">Name</label>
                    <Input
                      type="text"
                      value={newUserName}
                      onChange={(e) => setNewUserName(e.target.value)}
                      placeholder="Customer name"
                    />
                  </div>
                </div>
                <div className="flex gap-3 items-end">
                  <div className="w-32">
                    <label className="text-sm font-medium mb-2 block">Families</label>
                    <Input
                      type="number"
                      value={newNumFamilies}
                      onChange={(e) => setNewNumFamilies(e.target.value)}
                      placeholder="2"
                    />
                  </div>
                  <div className="w-56">
                    <label className="text-sm font-medium mb-2 block">Account Type</label>
                    <Select value={newAccountType} onValueChange={setNewAccountType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="freeHearthOwningAccount">Free</SelectItem>
                        <SelectItem value="payingHearthOwningAccount">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-40">
                    <label className="text-sm font-medium mb-2 block">Batch</label>
                    <Input
                      type="text"
                      value={newBatch}
                      onChange={(e) => setNewBatch(e.target.value)}
                      placeholder="e.g. beta1"
                    />
                  </div>
                  <div className="w-40">
                    <label className="text-sm font-medium mb-2 block">Country</label>
                    <Select value={newCountry} onValueChange={setNewCountry}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GB">UK</SelectItem>
                        <SelectItem value="US">US</SelectItem>
                        <SelectItem value="CA">Canada</SelectItem>
                        <SelectItem value="AT">Austria</SelectItem>
                        <SelectItem value="BE">Belgium</SelectItem>
                        <SelectItem value="BG">Bulgaria</SelectItem>
                        <SelectItem value="HR">Croatia</SelectItem>
                        <SelectItem value="CY">Cyprus</SelectItem>
                        <SelectItem value="CZ">Czechia</SelectItem>
                        <SelectItem value="DK">Denmark</SelectItem>
                        <SelectItem value="EE">Estonia</SelectItem>
                        <SelectItem value="FI">Finland</SelectItem>
                        <SelectItem value="FR">France</SelectItem>
                        <SelectItem value="DE">Germany</SelectItem>
                        <SelectItem value="GR">Greece</SelectItem>
                        <SelectItem value="HU">Hungary</SelectItem>
                        <SelectItem value="IE">Ireland</SelectItem>
                        <SelectItem value="IT">Italy</SelectItem>
                        <SelectItem value="LV">Latvia</SelectItem>
                        <SelectItem value="LT">Lithuania</SelectItem>
                        <SelectItem value="LU">Luxembourg</SelectItem>
                        <SelectItem value="MT">Malta</SelectItem>
                        <SelectItem value="NL">Netherlands</SelectItem>
                        <SelectItem value="PL">Poland</SelectItem>
                        <SelectItem value="PT">Portugal</SelectItem>
                        <SelectItem value="RO">Romania</SelectItem>
                        <SelectItem value="SK">Slovakia</SelectItem>
                        <SelectItem value="SI">Slovenia</SelectItem>
                        <SelectItem value="ES">Spain</SelectItem>
                        <SelectItem value="SE">Sweden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="default"
                    onClick={handleAddUser}
                    disabled={isAddingUser}
                    className="flex items-center gap-2 self-end"
                  >
                    <Plus className="w-4 h-4" />
                    {isAddingUser ? 'Adding...' : 'Add Account'}
                  </Button>
                </div>
              </div>

              {errorMessage && (
                <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 text-destructive">
                  <AlertTriangle className="w-4 h-4" />
                  {errorMessage}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Import Accounts Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Import Accounts from File
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-3 items-end max-w-2xl">
                  <div className="w-40">
                    <label className="text-sm font-medium mb-2 block">File Type</label>
                    <Select value={importFileType} onValueChange={setImportFileType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="csv">CSV</SelectItem>
                        <SelectItem value="json">JSON</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-2 block">Account Type</label>
                    <Select value={importAccountType} onValueChange={setImportAccountType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="freeHearthOwningAccount">Free Hearth Owning Account</SelectItem>
                        <SelectItem value="payingHearthOwningAccount">Paying Hearth Owning Account</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-2 block">Select File</label>
                    <div className="relative">
                      <input
                        type="file"
                        accept={importFileType === 'csv' ? '.csv' : '.json'}
                        onChange={handleImportAccounts}
                        disabled={isImporting}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-900 file:text-white hover:file:bg-gray-800 disabled:opacity-50"
                      />
                    </div>
                  </div>
                </div>

                {isImporting && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-700">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                    Importing accounts...
                  </div>
                )}

                {importMessage && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    {importMessage}
                  </div>
                )}

                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    File Format Information:
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1 ml-6 list-disc">
                    <li>CSV: First row must contain headers (email, numOfFamilies, nameAtOrder, shippingAddressAtOrder, shippingCountryAtOrder)</li>
                    <li>JSON objects like shippingAddressAtOrder should be formatted as valid JSON within the CSV cell</li>
                    <li>JSON: Array of account objects with the same fields</li>
                    <li>Each account will be assigned a UUID and accountStatus will be set to "accountCreated"</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Devices Tab */}
        <TabsContent value="devices" className="space-y-6">
          <Card>
            <CardHeader className="py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Monitor className="w-5 h-5" />
                  Device Management
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">Sort:</span>
                    <Select value={deviceSortMode} onValueChange={(value) => {
                      setDeviceSortMode(value);
                      if (value === 'recentIgloo' && Object.keys(lastIglooTimes).length === 0) fetchIglooTimes();
                    }}>
                      <SelectTrigger className="h-7 w-[150px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="alphabetic">Alphabetic</SelectItem>
                        <SelectItem value="recentSetup">Recent Setup</SelectItem>
                        <SelectItem value="recentSignin">Recent Signin</SelectItem>
                        <SelectItem value="recentIgloo">Recent Igloo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={expandAllDeviceAccounts}
                    disabled={Object.keys(expandedDeviceAccounts).filter(k => expandedDeviceAccounts[k]).length === deviceAccounts.length}
                    className="h-7 px-2 text-xs"
                  >
                    <ChevronDown className="w-3.5 h-3.5 mr-1" />
                    Expand All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={collapseAllDeviceAccounts}
                    disabled={Object.keys(expandedDeviceAccounts).filter(k => expandedDeviceAccounts[k]).length === 0}
                    className="h-7 px-2 text-xs"
                  >
                    <ChevronRight className="w-3.5 h-3.5 mr-1" />
                    Collapse All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {deviceAccounts.length === 0 && devices.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Monitor className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No devices found.</p>
                  </div>
                ) : (
                  <>
                    {iglooTimesLoading && (
                      <div className="text-center py-2 text-xs text-muted-foreground">Loading igloo data...</div>
                    )}
                    {/* Render accounts with their devices */}
                    {sortedDeviceAccounts.map(account => (
                      <DeviceAccountGroup
                        key={account.id}
                        account={account}
                        accountDevices={getDevicesForAccount(account)}
                        isExpanded={!!expandedDeviceAccounts[account.id]}
                        onToggle={toggleDeviceAccount}
                        onViewAccountDocument={setDocViewerTarget}
                        sharedProps={{
                          availableAppVersions,
                          availableOsVersions,
                          onTargetVersionChange: handleTargetVersionChange,
                          onInstantUpdateChange: handleInstantUpdateChange,
                          onTargetOsVersionChange: handleTargetOsVersionChange,
                          onInstantOsUpdateChange: handleInstantOsUpdateChange,
                          onViewInstantLog: handleViewInstantLog,
                          onViewHotplug: handleViewHotplug,
                          onViewOverrideSettings: handleViewOverrideSettings,
                          onViewTvInfo: handleViewTvInfo,
                          onViewLog: handleViewLog,
                          onRequestLogDump: handleRequestLogDump,
                          onViewCrashLog: handleViewCrashLog,
                          onViewDocument: setDocViewerTarget,
                          onViewAlerts: handleViewAlerts,
                          hotplugFamilies,
                          overrideFamilies,
                          alertUnreadByFamily,
                          deviceRssi,
                          deviceLocation,
                          iglooNames,
                          firstSigninTimes,
                        }}
                      />
                    ))}

                    {/* Orphaned Devices (not belonging to any account) */}
                    <OrphanedDevicesGroup
                      orphanedDevices={orphanedDevices}
                      sharedProps={{
                        availableAppVersions,
                        availableOsVersions,
                        onTargetVersionChange: handleTargetVersionChange,
                        onInstantUpdateChange: handleInstantUpdateChange,
                        onTargetOsVersionChange: handleTargetOsVersionChange,
                        onInstantOsUpdateChange: handleInstantOsUpdateChange,
                        onViewInstantLog: handleViewInstantLog,
                        onViewHotplug: handleViewHotplug,
                        onViewOverrideSettings: handleViewOverrideSettings,
                        onViewTvInfo: handleViewTvInfo,
                        onViewLog: handleViewLog,
                        onViewDocument: setDocViewerTarget,
                        hotplugFamilies,
                        overrideFamilies,
                        deviceRssi,
                        deviceLocation,
                        iglooNames,
                        firstSigninTimes,
                      }}
                    />
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Igloos Tab */}
        <TabsContent value="igloos" className="space-y-6">
          <Card>
            <CardHeader className="py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Snowflake className="w-5 h-5" /> Igloos Management
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={expandAllIglooAccounts}>Expand All</Button>
                  <Button variant="outline" size="sm" onClick={collapseAllIglooAccounts}>Collapse All</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {iglooAccountsError && (
                <p className="text-sm text-destructive mb-2">
                  Couldn't load account grouping (accountsInfo read failed) — igloos below may all appear under "no owning account". See the browser console.
                </p>
              )}
              {!(igloosLoaded && iglooAccountsLoaded) ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-transparent rounded-full animate-spin" />
                  Loading igloos…
                </div>
              ) : (
              <div className="space-y-2">
                {iglooAccountGroups.accounts.map(({ account, igloos: accountIgloos }) => (
                  <IglooAccountGroup
                    key={account.id}
                    account={account}
                    igloos={accountIgloos}
                    isExpanded={!!expandedIglooAccounts[account.id]}
                    onToggle={toggleIglooAccount}
                    onViewAccountDocument={setDocViewerTarget}
                    liveSessionsByIgloo={liveSessionsByIgloo}
                    overrideIgloos={overrideIgloos}
                    onViewSettings={handleViewIglooSettings}
                    onViewMetrics={handleViewIglooMetrics}
                  />
                ))}
                {iglooAccountGroups.orphaned.length > 0 && (
                  <IglooAccountGroup
                    key="__orphaned__"
                    account={{ id: 'orphaned', email: 'Igloos with no owning account' }}
                    igloos={iglooAccountGroups.orphaned}
                    isExpanded={!!expandedIglooAccounts.orphaned}
                    isOrphan
                    onToggle={toggleIglooAccount}
                    onViewAccountDocument={() => {}}
                    liveSessionsByIgloo={liveSessionsByIgloo}
                    overrideIgloos={overrideIgloos}
                    onViewSettings={handleViewIglooSettings}
                    onViewMetrics={handleViewIglooMetrics}
                  />
                )}
                {iglooAccountGroups.accounts.length === 0 && iglooAccountGroups.orphaned.length === 0 && (
                  <p className="text-sm text-muted-foreground">No igloos found.</p>
                )}
              </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Health Tab */}
        <TabsContent value="health" className="space-y-6">
          <HealthTab />
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="space-y-6">
          <SessionsTab />
        </TabsContent>

        {/* Status Tab */}
        <TabsContent value="status" className="space-y-6">
          <StatusTab />
        </TabsContent>
      </Tabs>

      {/* Full-screen Alerts Viewer (per-device or cross-family inbox) */}
      {alertsViewerCtx && (
        <AlertsViewer
          familyId={alertsViewerCtx.familyId}
          familyName={alertsViewerCtx.familyName}
          onClose={handleCloseAlerts}
        />
      )}

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                Confirm Removal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-6">Are you sure you want to remove <strong>{selectedUser?.email}</strong>?</p>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleRemoveUser(selectedUser.email)}
                  disabled={isRemoving}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  {isRemoving ? 'Removing...' : 'Remove'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reset Account Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Reset Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-3">
                Reset <strong>{resetUser?.email}</strong> back to the not-yet-set-up state?
              </p>
              <div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900">
                <p className="font-medium mb-1">This will permanently:</p>
                <ul className="list-disc pl-5 space-y-0.5">
                  <li>Delete this account's families, igloos, and device data</li>
                  <li>Remove this account from any families it was a controller of</li>
                  <li>Clear the account's display name and family list</li>
                  {resetShipping && (
                    <>
                      <li>Clear the user-confirmed shipping addresses</li>
                      <li>Reset shipped, preparing-for-shipping, tracking, and delivery flags</li>
                    </>
                  )}
                </ul>
                <p className="mt-2">
                  The sign-in (Firebase Auth user) and order metadata{!resetShipping && ' (including shipping status)'} are preserved.
                  This action cannot be undone.
                </p>
              </div>
              <label className="mb-6 flex items-start gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={resetShipping}
                  onChange={(e) => setResetShipping(e.target.checked)}
                  disabled={isResetting}
                />
                <span>
                  <span className="font-medium">Also reset shipping &amp; fulfillment</span>
                  <span className="block text-xs text-muted-foreground">
                    Roll the account back to the post-add state, before the user confirmed addresses or any device was shipped.
                  </span>
                </span>
              </label>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => { setShowResetModal(false); setResetUser(null); setResetShipping(false); }}
                  disabled={isResetting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleResetUser(resetUser.email)}
                  disabled={isResetting}
                  className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <RotateCcw className="w-4 h-4" />
                  {isResetting ? 'Resetting...' : 'Reset'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Ship & Subscribe Modal */}
      {showShipSubscribeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Mark as Shipped
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shipUpdateDate}
                    onChange={(e) => setShipUpdateDate(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Update shipped date to now</span>
                </label>
              </div>
              <p className="mb-6">Also subscribe this customer to email updates?</p>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => { setShowShipSubscribeModal(false); setShipSubscribeUserId(null); setShipUpdateDate(true); }}>
                  Cancel
                </Button>
                <Button variant="outline" onClick={() => handleShipWithSubscription(false)}>
                  Ship only
                </Button>
                <Button onClick={() => handleShipWithSubscription(true)}>
                  Ship & subscribe
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Log Viewer */}
      {logModalVisible && (
        <LogViewer
          logContent={logContent}
          logName={currentLogName}
          loading={logLoading}
          reloading={logReloading}
          onClose={() => { setLogModalVisible(false); setLogReloading(false); setIsCrashLog(false); }}
          onReload={isCrashLog ? handleCrashLogViewerReload : handleLogViewerReload}
        />
      )}

      {/* Support Chat Modal */}
      {showSupportChat && selectedChatUser && (
        <AdminSupportChat
          accountUUID={selectedChatUser.id}
          userEmail={selectedChatUser.email}
          userName={selectedChatUser.name}
          onClose={handleCloseChat}
        />
      )}

      {/* Document Viewer Modal */}
      {docViewerTarget && (
        <DocumentViewer
          initialType={docViewerTarget.type}
          initialId={docViewerTarget.id}
          onClose={() => setDocViewerTarget(null)}
        />
      )}

      {/* Billing Modal */}
      {billingCheckTarget && (
        <BillingModal
          accountUUID={billingCheckTarget.id}
          accountEmail={billingCheckTarget.email}
          onClose={() => setBillingCheckTarget(null)}
          onChanged={handleBillingChanged}
        />
      )}

      {/* Full-screen Message Inbox */}
      {showMessageInbox && (
        <MessageInbox
          conversations={conversations}
          users={users}
          drafts={adminDrafts}
          onClose={() => setShowMessageInbox(false)}
        />
      )}
    </div>
  );
}

function parseOtaStatus(otaStatus) {
  if (otaStatus === undefined || otaStatus === null) return '';
  if (typeof otaStatus !== 'string') return String(otaStatus);

  try {
    const obj = JSON.parse(otaStatus);
    const status = obj.status;
    const version = obj.version;

    return (
      <>
        {status && <div className="text-xs">{status}</div>}
        {version && <div className="text-xs">{version}</div>}
      </>
    );
  } catch (e) {
    return otaStatus;
  }
}

export default AdminDashboard;
